import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSidebar } from '@/context/SidebarContext';
import { API } from '@/lib/api';
import { useResizablePanel } from '@/hooks/useResizablePanel';
import { buildSavedItemWorkspace } from '@/lib/savedItemMetadata';
import {
  clearResearchWorkspaceDraft,
  ensureAgentWorkspace,
  fetchAgentWorkspace,
  loadResearchWorkspaceDraft,
  saveResearchWorkspaceDraft,
  updateResearchWorkspaceState,
} from '@/lib/agentWorkspace';
import { readEventStream } from '@/lib/sse';
import {
  buildResearchDeskModel,
  getWorkflowModeDetails,
} from '@/lib/researchDesk';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown as TiptapMarkdown } from 'tiptap-markdown';
import { toast } from 'sonner';
import CollaborationStrip from '@/components/CollaborationStrip';
import JollyDictationButton from '@/components/JollyDictationButton';
import FeatureHistoryMenu from '@/components/FeatureHistoryMenu';
import MatterWorkspaceContext, { withQueryParams } from '@/components/matter/MatterWorkspaceContext';
import { buildAgentHandoffRecommendation } from '@/lib/agentRouting';
import { recordJourneyVisit } from '@/lib/chatJourney';
import {
  Search, ExternalLink, Loader2, FileText, Plus, Trash2, ChevronRight,
  Send, Download, Save, Wand2, X, CheckCircle, Sparkles,
  BookOpen, List, Bot, Gavel, Hash, Target, PenLine, Scale, Zap,
  RotateCcw, Copy, ArrowUp, ArrowDown, Globe, Upload, Database, File, MessageSquare,
  RefreshCw, Tag, Paperclip, Mic,
} from 'lucide-react';
import LinkPreviewPanel from '@/components/LinkPreviewPanel';
import SourceTrustBadge from '@/components/SourceTrustBadge';
import { buildTrustSummary, dedupeSourceList } from '@/lib/sourceTrust';

function getUploadPreviewUrl(uploadId) {
  return `${API}/uploads/${uploadId}/preview`;
}

const RESEARCH_TYPES = [
  { value: 'TOPIC', label: 'Legal Issue', icon: BookOpen, prompt: 'Research a broad issue, dispute, or legal position.' },
  { value: 'CASE', label: 'Case', icon: Scale, prompt: 'Trace holdings, ratio, and later treatment for a judgment.' },
  { value: 'STATUTE', label: 'Statute', icon: Hash, prompt: 'Interpret an Act, section, rule, or notification.' },
  { value: 'PERSON', label: 'Person', icon: FileText, prompt: 'Track a judge, counsel, litigant, or authority across matters.' },
];

const WORKSPACE_FLOWS = [
  { value: 'MEMO', label: 'Desk Note', icon: BookOpen, template: 'LEGAL_MEMO', helper: 'Best for advice notes, opinions, and client-ready analysis.' },
  { value: 'HEARING', label: 'Hearing Note', icon: Scale, template: 'HEARING_NOTE', helper: 'Pull authorities, issues, and likely pushback into one sheet.' },
  { value: 'BRIEF', label: 'Case Brief', icon: FileText, template: 'CASE_BRIEF', helper: 'Useful when you need a clean digest of one key judgment.' },
  { value: 'PETITION', label: 'Petition Grounds', icon: Gavel, template: 'PETITION', helper: 'Start with grounds and relief when litigation drafting is the goal.' },
  { value: 'BENCH', label: 'Bench Questions', icon: MessageSquare, template: 'BENCH_MEMO', helper: 'Pressure-test likely judicial questions and your cleanest answers.' },
  { value: 'IRAC', label: 'IRAC Answer', icon: Zap, template: 'IRAC', helper: 'Fast structured analysis for problem questions and issue spotting.' },
];

const COURT_OPTIONS = [
  { value: 'GENERAL', label: 'Any forum' },
  { value: 'SC', label: 'Supreme Court' },
  { value: 'HC', label: 'High Court' },
  { value: 'TRIBUNAL', label: 'Tribunal' },
  { value: 'TRIAL', label: 'Trial / Sessions' },
];

const MATTER_STARTERS = [
  'Anticipatory bail after interim protection was vacated',
  'Section 138 NI Act defence where cheque was issued as security',
  'Maintainability of writ against a private body performing public duties',
  'Enforcement of an arbitration clause despite parallel civil proceedings',
  'Specific performance claim where limitation and readiness are disputed',
  'Service law challenge to suspension pending disciplinary inquiry',
];

function createTemplateSections(templateKey) {
  return (TEMPLATES[templateKey] || TEMPLATES.CUSTOM).sections.map(section => ({ ...section, content: '' }));
}

function buildDocumentTitle(topic, flowValue) {
  const flow = WORKSPACE_FLOWS.find(item => item.value === flowValue);
  if (!topic) return 'Untitled Research Document';
  if (!flow) return topic;
  if (flow.value === 'HEARING') return `${topic} - Hearing Note`;
  if (flow.value === 'PETITION') return `${topic} - Petition Grounds`;
  if (flow.value === 'BRIEF') return `${topic} - Case Brief`;
  if (flow.value === 'BENCH') return `${topic} - Bench Questions`;
  if (flow.value === 'IRAC') return `${topic} - IRAC Analysis`;
  return `${topic} - Research Memo`;
}

function normalizeResearchSource(source, fallbackGroup = 'Research') {
  return {
    ...source,
    id: source.tid || source.id || `src_${Math.random().toString(36).slice(2, 10)}`,
    group: source.group || fallbackGroup,
    trust_label: source.trust_label || source.trustLabel || (String(source.url || '').includes('indiankanoon.org') ? 'Verified Authority' : 'External / Unverified'),
    authority_strength_label: source.authority_strength_label || source.authorityStrengthLabel || '',
    reason: source.reason || '',
    source_family: source.source_family || source.sourceFamily || (String(source.url || '').includes('indiankanoon.org') ? 'indiankanoon' : 'external'),
  };
}

function mergeSourceLists(existing, incoming) {
  const seen = new Set(existing.map(item => item.id));
  return [...existing, ...incoming.filter(item => !seen.has(item.id))];
}

function buildResearchWorkspaceSnapshot({
  researchTopic,
  researchType,
  workflowMode,
  primaryCourt,
  docTitle,
  docTemplate,
  docSections,
  agentMessages,
  activeSources,
  sources,
  pinnedSources,
  sourceFilter,
  sourcesQuery,
  activeUserDocIds,
  kickoffReport,
  kickoffCases,
  kickoffUserDocs,
  decomposedIssues,
  researchIntelligence,
}) {
  return {
    query: researchTopic,
    research_type: researchType,
    workflow_mode: workflowMode,
    primary_court: primaryCourt,
    document_title: docTitle,
    template_type: docTemplate,
    document_sections: docSections,
    agent_messages: agentMessages,
    active_sources: activeSources,
    sources,
    pinned_sources: pinnedSources,
    source_filter: sourceFilter,
    sources_query: sourcesQuery,
    active_user_doc_ids: activeUserDocIds,
    kickoff_report: kickoffReport,
    kickoff_cases: kickoffCases,
    kickoff_user_docs: kickoffUserDocs,
    decomposed_issues: decomposedIssues,
    research_intelligence: researchIntelligence,
  };
}

// ─── Document templates ───────────────────────────────────────────────────────
const TEMPLATES = {
  CASE_BRIEF: {
    label: 'Case Brief',
    emoji: '⚖️',
    sections: [
      { id: 'facts', title: 'Facts of the Case', placeholder: 'Describe the key facts chronologically...' },
      { id: 'issues', title: 'Legal Issues', placeholder: 'What legal questions were before the court?' },
      { id: 'holdings', title: 'Holdings', placeholder: 'What did the court decide on each issue?' },
      { id: 'reasoning', title: 'Reasoning & Ratio Decidendi', placeholder: 'Why did the court rule this way? What principle was established?' },
      { id: 'significance', title: 'Legal Significance', placeholder: 'Impact on Indian law, subsequent treatment, notable application...' },
    ],
  },
  LEGAL_MEMO: {
    label: 'Legal Memorandum',
    emoji: '📋',
    sections: [
      { id: 'question', title: 'Question Presented', placeholder: 'State the precise legal question...' },
      { id: 'brief_answer', title: 'Brief Answer', placeholder: 'Concise answer in 2–3 sentences...' },
      { id: 'facts', title: 'Statement of Facts', placeholder: 'Relevant background and facts...' },
      { id: 'discussion', title: 'Discussion', placeholder: 'Detailed analysis of the applicable law...' },
      { id: 'conclusion', title: 'Conclusion', placeholder: 'Summary, recommendation, and next steps...' },
    ],
  },
  RESEARCH_PAPER: {
    label: 'Research Paper',
    emoji: '📄',
    sections: [
      { id: 'abstract', title: 'Abstract', placeholder: '150–200 word summary of the paper...' },
      { id: 'introduction', title: 'Introduction', placeholder: 'Background, objectives, and scope...' },
      { id: 'framework', title: 'Legal Framework', placeholder: 'Constitutional & statutory provisions...' },
      { id: 'analysis', title: 'Analysis & Discussion', placeholder: 'In-depth legal analysis with case citations...' },
      { id: 'conclusion', title: 'Conclusion', placeholder: 'Findings, gaps, and recommendations...' },
      { id: 'references', title: 'References', placeholder: 'All citations and bibliography...' },
    ],
  },
  IRAC: {
    label: 'Exam Answer (IRAC)',
    emoji: '🎓',
    sections: [
      { id: 'issue', title: 'Issue', placeholder: 'State the legal issue precisely...' },
      { id: 'rule', title: 'Rule', placeholder: 'State the applicable law (Act, section, case law)...' },
      { id: 'application', title: 'Application', placeholder: 'Apply the law to the specific facts...' },
      { id: 'conclusion', title: 'Conclusion', placeholder: 'State your conclusion...' },
    ],
  },
  PETITION: {
    label: 'Petition Draft',
    emoji: '🏛️',
    sections: [
      { id: 'parties', title: 'Parties', placeholder: 'Petitioner and Respondent details...' },
      { id: 'jurisdiction', title: 'Jurisdiction', placeholder: 'Basis of the court\'s jurisdiction...' },
      { id: 'facts', title: 'Statement of Facts', placeholder: 'Chronological facts of the case...' },
      { id: 'grounds', title: 'Grounds for Relief', placeholder: 'Legal grounds supporting the petition...' },
      { id: 'prayer', title: 'Prayer / Relief Sought', placeholder: 'Specific relief requested from the court...' },
    ],
  },
  HEARING_NOTE: {
    label: 'Hearing Note',
    emoji: '🎯',
    sections: [
      { id: 'matter_snapshot', title: 'Matter Snapshot', placeholder: 'Capture the factual posture, stage, and relief sought...' },
      { id: 'questions_for_hearing', title: 'Questions for Hearing', placeholder: 'List the legal and factual questions the court is likely to press on...' },
      { id: 'binding_authorities', title: 'Binding Authorities', placeholder: 'Add the strongest Supreme Court / jurisdictional High Court authorities...' },
      { id: 'opponent_case', title: 'Likely Opposition Position', placeholder: 'What is the other side likely to argue and which cases might they cite?' },
      { id: 'oral_notes', title: 'Oral Notes & Relief', placeholder: 'Key speaking points, fallback positions, and exact relief to press...' },
    ],
  },
  BENCH_MEMO: {
    label: 'Bench Questions',
    emoji: '🧭',
    sections: [
      { id: 'issue_frame', title: 'Issue Frame', placeholder: 'State the clean legal issue the bench will engage with first...' },
      { id: 'likely_questions', title: 'Likely Bench Questions', placeholder: 'List the most likely questions from the bench in plain courtroom language...' },
      { id: 'best_answers', title: 'Best Answers', placeholder: 'Provide the cleanest answer with one-line authority support...' },
      { id: 'fallbacks', title: 'Fallback Positions', placeholder: 'If the primary answer is resisted, what is the narrower fallback?' },
      { id: 'pressure_points', title: 'Pressure Points', placeholder: 'Note factual, procedural, or relief vulnerabilities the bench may press on...' },
    ],
  },
  CUSTOM: {
    label: 'Blank Document',
    emoji: '📝',
    sections: [
      { id: 'section_1', title: 'Section 1', placeholder: 'Start writing...' },
    ],
  },
};

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ResearchAgentPage() {
  const { user, getAuthHeaders } = useAuth();
  const { isMobile } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const workspaceId = searchParams.get('workspace_id');
  const contextPanel = searchParams.get('context_panel') || '';
  const journeyKey = searchParams.get('journey_key') || '';
  const ainaReturnPath = searchParams.get('aina_return_path') || '';
  const showBackToAina = searchParams.get('return_to') === 'aina' && Boolean(ainaReturnPath);
  const { width: leftPanelWidth, startDrag: startLeftDrag } = useResizablePanel(295, { min: 180, max: 520 });
  const { width: rightPanelWidth, startDrag: startRightDrag } = useResizablePanel(370, { min: 220, max: 560 });

  // Topic
  const [researchTopic, setResearchTopic] = useState('');
  const [workspaceStarted, setWorkspaceStarted] = useState(false);
  const [showDeskCounsel, setShowDeskCounsel] = useState(false);
  const [researchType, setResearchType] = useState('TOPIC');
  const [workflowMode, setWorkflowMode] = useState('MEMO');
  const [primaryCourt, setPrimaryCourt] = useState('GENERAL');
  const [linkedDoc, setLinkedDoc] = useState(null);
  const [matterContext, setMatterContext] = useState(null);
  const [kickoffReport, setKickoffReport] = useState('');
  const [kickoffStatus, setKickoffStatus] = useState('');
  const [kickoffResearching, setKickoffResearching] = useState(false);
  const [kickoffCases, setKickoffCases] = useState([]);
  const [kickoffUserDocs, setKickoffUserDocs] = useState([]);
  const kickoffAbortRef = useRef(null);
  const autoStartDoneRef = useRef(false);

  // Sources
  const [sourcesQuery, setSourcesQuery] = useState('');
  const [sources, setSources] = useState([]);
  const [searchingSources, setSearchingSources] = useState(false);
  const [pinnedSources, setPinnedSources] = useState([]);
  const [activeSources, setActiveSources] = useState([]);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [expandedSource, setExpandedSource] = useState(null);
  const [decomposedIssues, setDecomposedIssues] = useState([]);
  const [contextDebug, setContextDebug] = useState([]);
  const [sourceSummary, setSourceSummary] = useState({});
  const [researchIntelligence, setResearchIntelligence] = useState({});

  // Document
  const [docTitle, setDocTitle] = useState('Untitled Research Document');
  const [docTemplate, setDocTemplate] = useState('CUSTOM');
  const [docSections, setDocSections] = useState(
    TEMPLATES.CUSTOM.sections.map(s => ({ ...s, content: '' }))
  );
  const [showTOC, setShowTOC] = useState(false);
  const [savingDoc, setSavingDoc] = useState(false);
  const [generatingSection, setGeneratingSection] = useState(null);
  const sectionRefs = useRef({});
  const docScrollRef = useRef(null);

  // Left panel tab
  const [leftTab, setLeftTab] = useState('sources'); // 'sources' | 'knowledge'

  // Knowledge base (user uploads)
  const [userDocs, setUserDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [activeUserDocIds, setActiveUserDocIds] = useState([]);
  const [docsDragOver, setDocsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const chatFileInputRef = useRef(null);
  const [chatPendingFile, setChatPendingFile] = useState(null);
  const [chatUploadingFile, setChatUploadingFile] = useState(false);

  // Agent
  const [agentMessages, setAgentMessages] = useState([
    {
      role: 'agent',
      content:
        "Start with the legal issue, the judgment, or the drafting task in front of you. I’ll help you build a research desk with an issue map, authority stack, verified Indian Kanoon and government sources, your own uploaded documents, and draft-ready work product in the middle.\n\nUse the kickoff pane to open a desk note, hearing note, petition grounds sheet, case brief, or bench question mode, then ask me to stress-test the position, surface missing authority, or draft sections.",
      type: 'intro',
    },
  ]);
  const [agentInput, setAgentInput] = useState('');
  const [agentStreaming, setAgentStreaming] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [workspace, setWorkspace] = useState(null);
  const openPreview = useCallback((url, title = '') => { setPreviewUrl(url); setPreviewTitle(title); }, []);
  const agentAbortRef = useRef(null);
  const agentChatEndRef = useRef(null);
  const agentInputRef = useRef(null);
  const workspaceRestoreKeyRef = useRef('');
  const focusCueAppliedRef = useRef('');

  useEffect(() => {
    document.title = 'Research — Advoq';
  }, []);

  useEffect(() => {
    if (!journeyKey) return;
    recordJourneyVisit(journeyKey, {
      surface: 'RESEARCH_AGENT',
      label: 'Research',
      href: `${location.pathname}${location.search}`,
    });
  }, [journeyKey, location.pathname, location.search]);

  // Auto-scroll agent chat
  useEffect(() => {
    agentChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [agentMessages]);

  const buildWorkspaceSearchString = useCallback((overrides = {}) => {
    const params = new URLSearchParams(searchParams.toString());
    const nextWorkspaceId = overrides.workspaceId ?? workspace?.workspace_id ?? workspaceId;
    const nextLinkedDoc = overrides.linkedDoc ?? linkedDoc;
    const nextMatterContext = overrides.matterContext ?? matterContext;
    const nextQuery = overrides.prefillQuery;
    const nextType = overrides.prefillType;

    if (nextWorkspaceId) params.set('workspace_id', nextWorkspaceId);
    else params.delete('workspace_id');

    if (nextLinkedDoc?.id && nextLinkedDoc?.type) {
      params.set('linked_doc_id', nextLinkedDoc.id);
      params.set('linked_doc_type', nextLinkedDoc.type);
      params.set('linked_doc_title', nextLinkedDoc.title || 'Linked document');
    } else {
      params.delete('linked_doc_id');
      params.delete('linked_doc_type');
      params.delete('linked_doc_title');
    }

    if (nextMatterContext?.id || nextMatterContext?.name) {
      params.set('matter_id', nextMatterContext?.id || nextMatterContext?.name);
      params.set('matter_name', nextMatterContext?.name || nextMatterContext?.id);
    } else {
      params.delete('matter_id');
      params.delete('matter_name');
    }

    if (nextQuery) params.set('prefill_query', nextQuery);
    if (nextType) params.set('prefill_type', nextType);

    return params.toString();
  }, [linkedDoc, matterContext, searchParams, workspace?.workspace_id, workspaceId]);

  const appendResearchTopic = useCallback((text) => {
    const cleaned = String(text || '').trim();
    if (!cleaned) return;
    setResearchTopic(prev => {
      const spacer = prev && !/[ \n]$/.test(prev) ? ' ' : '';
      return `${prev}${spacer}${cleaned}`.trimStart();
    });
  }, []);

  const appendAgentInput = useCallback((text) => {
    const cleaned = String(text || '').trim();
    if (!cleaned) return;
    setAgentInput(prev => {
      const spacer = prev && !/[ \n]$/.test(prev) ? ' ' : '';
      return `${prev}${spacer}${cleaned}`.trimStart();
    });
    agentInputRef.current?.focus();
  }, []);

  const buildWorkspaceResearchFallback = useCallback((workspacePayload) => {
    if (!workspacePayload || typeof workspacePayload !== 'object') return null;
    const latestChatEntry = [...(workspacePayload.chat_entries || [])]
      .filter(entry => entry && typeof entry === 'object')
      .sort((left, right) => String(right.created_at || '').localeCompare(String(left.created_at || '')))[0];
    if (!latestChatEntry) return null;

    const activity = String(workspacePayload.activity_type || '').toUpperCase();
    const workflowModeFallback = activity === 'DRAFTING'
      ? 'PETITION'
      : activity === 'STRATEGY'
        ? 'HEARING'
        : 'MEMO';
    const researchTypeFallback = activity === 'STATUTORY_RESEARCH'
      ? 'STATUTE'
      : activity === 'CASE_ANALYSIS'
        ? 'CASE'
        : 'TOPIC';
    const template = WORKSPACE_FLOWS.find(flow => flow.value === workflowModeFallback)?.template || 'LEGAL_MEMO';
    const query = latestChatEntry.prompt || workspacePayload.title || workspacePayload.overview_markdown || '';

    return {
      query,
      research_type: researchTypeFallback,
      workflow_mode: workflowModeFallback,
      template_type: template,
      document_title: buildDocumentTitle(query, workflowModeFallback),
      document_sections: createTemplateSections(template),
      active_sources: workspacePayload.authority_sources || [],
      kickoff_report: workspacePayload.overview_markdown || latestChatEntry.response || '',
      kickoff_cases: workspacePayload.authority_sources || [],
      agent_messages: [
        { role: 'system', content: 'Recovered from the shared workspace so research can continue without losing context.', type: 'workspace_restore' },
        { role: 'user', content: latestChatEntry.prompt || query },
        { role: 'agent', content: latestChatEntry.response || workspacePayload.overview_markdown || '' },
      ].filter(message => message.content),
    };
  }, []);

  const restoreResearchState = useCallback((state, workspacePayload = null) => {
    if (!state || typeof state !== 'object') return;

    const nextQuery = state.query || state.researchTopic || state.research_topic;
    const nextResearchType = state.research_type || state.researchType;
    const nextWorkflowMode = state.workflow_mode || state.workflowMode;
    const nextPrimaryCourt = state.primary_court || state.primaryCourt;
    const nextTemplate = state.template_type || state.template;
    const nextSections = state.document_sections || state.sections;
    const nextTitle = state.document_title || state.title;
    const nextAgentMessages = state.agent_messages || state.agentMessages;
    const nextActiveSources = state.active_sources || state.activeSources;
    const hasSources = Object.prototype.hasOwnProperty.call(state, 'sources');
    const hasPinnedSources = Object.prototype.hasOwnProperty.call(state, 'pinned_sources') || Object.prototype.hasOwnProperty.call(state, 'pinnedSources');
    const nextSources = state.sources || [];
    const nextPinnedSources = state.pinned_sources || state.pinnedSources || [];
    const nextSourceFilter = state.source_filter || state.sourceFilter;
    const nextSourcesQuery = state.sources_query || state.sourcesQuery;
    const nextDecomposedIssues = state.decomposed_issues || state.decomposedIssues;
    const nextResearchIntelligence = state.research_intelligence || state.researchIntelligence;

    if (nextTitle) setDocTitle(nextTitle);
    if (nextQuery) {
      setResearchTopic(nextQuery);
      setSourcesQuery(nextSourcesQuery || nextQuery);
    }
    if (nextResearchType && RESEARCH_TYPES.some(type => type.value === nextResearchType)) {
      setResearchType(nextResearchType);
    }
    if (nextWorkflowMode && WORKSPACE_FLOWS.some(flow => flow.value === nextWorkflowMode)) {
      setWorkflowMode(nextWorkflowMode);
    }
    if (nextPrimaryCourt && COURT_OPTIONS.some(option => option.value === nextPrimaryCourt)) {
      setPrimaryCourt(nextPrimaryCourt);
    }
    if (nextTemplate && TEMPLATES[nextTemplate]) {
      setDocTemplate(nextTemplate);
    }
    if (Array.isArray(nextSections) && nextSections.length > 0) {
      setDocSections(nextSections);
    }
    if (Array.isArray(nextAgentMessages) && nextAgentMessages.length > 0) {
      setAgentMessages(nextAgentMessages);
    }
    if (Array.isArray(nextActiveSources) && nextActiveSources.length > 0) {
      setActiveSources(nextActiveSources.map(source => normalizeResearchSource(source, source.group || 'Workspace')));
    }
    if (hasSources && Array.isArray(nextSources)) {
      setSources(nextSources.map(source => normalizeResearchSource(source, source.group || 'Workspace')));
    } else if (Array.isArray(nextActiveSources) && nextActiveSources.length > 0) {
      setSources(prev => mergeSourceLists(prev, nextActiveSources.map(source => normalizeResearchSource(source, source.group || 'Workspace'))));
    }
    if (hasPinnedSources && Array.isArray(nextPinnedSources)) {
      setPinnedSources(nextPinnedSources.map(source => normalizeResearchSource(source, source.group || 'Pinned')));
    }
    if (nextSourceFilter) setSourceFilter(nextSourceFilter);
    if (nextSourcesQuery) setSourcesQuery(nextSourcesQuery);
    if (Array.isArray(state.active_user_doc_ids) && state.active_user_doc_ids.length > 0) {
      setActiveUserDocIds(state.active_user_doc_ids);
    }
    if (state.kickoff_report) setKickoffReport(state.kickoff_report);
    if (Array.isArray(state.kickoff_cases)) setKickoffCases(state.kickoff_cases.map(source => normalizeResearchSource(source, 'Verified brief')));
    if (Array.isArray(state.kickoff_user_docs)) setKickoffUserDocs(state.kickoff_user_docs);
    if (Array.isArray(nextDecomposedIssues)) setDecomposedIssues(nextDecomposedIssues);
    if (nextResearchIntelligence && typeof nextResearchIntelligence === 'object') setResearchIntelligence(nextResearchIntelligence);
    else setResearchIntelligence({});
    if (workspacePayload?.linked_doc) setLinkedDoc(workspacePayload.linked_doc);
    if (workspacePayload?.matter_context) setMatterContext(workspacePayload.matter_context);
    setWorkspaceStarted(Boolean(
      nextQuery
      || nextTitle
      || (Array.isArray(nextSections) && nextSections.length > 0)
      || state.kickoff_report
    ));
  }, []);

  const loadWorkspaceState = useCallback(async (targetWorkspaceId) => {
    if (!targetWorkspaceId) {
      setWorkspace(null);
      return null;
    }
    try {
      const nextWorkspace = await fetchAgentWorkspace({
        workspaceId: targetWorkspaceId,
        headers: getAuthHeaders(),
      });
      setWorkspace(nextWorkspace);
      const backendState = nextWorkspace.research_state || {};
      const localDraft = loadResearchWorkspaceDraft(targetWorkspaceId);
      const backendStamp = backendState.updated_at || '';
      const localStamp = localDraft?.saved_at || '';
      const fallbackState = buildWorkspaceResearchFallback(nextWorkspace);
      const preferredState = localStamp && localStamp > backendStamp
        ? localDraft
        : (Object.keys(backendState).length > 0 ? backendState : fallbackState);
      const restoreKey = `${targetWorkspaceId}:${preferredState?.updated_at || preferredState?.saved_at || backendStamp || 'initial'}`;
      if (preferredState && workspaceRestoreKeyRef.current !== restoreKey) {
        restoreResearchState(preferredState, nextWorkspace);
        workspaceRestoreKeyRef.current = restoreKey;
      }
      return nextWorkspace;
    } catch {
      return null;
    }
  }, [buildWorkspaceResearchFallback, getAuthHeaders, restoreResearchState]);

  const ensureWorkspaceForResearch = useCallback(async (seedText) => {
    try {
      const ensured = await ensureAgentWorkspace({
        headers: getAuthHeaders(),
        payload: {
          source_agent: 'RESEARCH_AGENT',
          seed_text: seedText,
          workspace_id: workspace?.workspace_id || workspaceId || null,
          linked_doc_id: linkedDoc?.id || null,
          linked_doc_type: linkedDoc?.type || null,
          linked_doc_title: linkedDoc?.title || null,
          matter_id: matterContext?.id || null,
          matter_name: matterContext?.name || null,
        },
      });
      setWorkspace(ensured);
      const query = buildWorkspaceSearchString({ workspaceId: ensured.workspace_id });
      window.history.replaceState(null, '', `/research${query ? `?${query}` : ''}`);
      return ensured;
    } catch {
      toast.error('Failed to open shared workspace');
      return null;
    }
  }, [buildWorkspaceSearchString, getAuthHeaders, linkedDoc, matterContext, workspace?.workspace_id, workspaceId]);

  const persistWorkspaceSnapshot = useCallback(async (targetWorkspaceId = null) => {
    const resolvedWorkspaceId = targetWorkspaceId || workspace?.workspace_id || workspaceId;
    if (!resolvedWorkspaceId) return null;
    const snapshot = buildResearchWorkspaceSnapshot({
      researchTopic,
      researchType,
      workflowMode,
      primaryCourt,
      docTitle,
      docTemplate,
      docSections,
      agentMessages,
      activeSources,
      sources,
      pinnedSources,
      sourceFilter,
      sourcesQuery,
      activeUserDocIds,
      kickoffReport,
      kickoffCases,
      kickoffUserDocs,
      decomposedIssues,
      researchIntelligence,
    });
    saveResearchWorkspaceDraft(resolvedWorkspaceId, snapshot);
    try {
      return await updateResearchWorkspaceState({
        workspaceId: resolvedWorkspaceId,
        headers: getAuthHeaders(),
        payload: {
          research_state: snapshot,
          linked_doc_id: linkedDoc?.id || null,
          linked_doc_type: linkedDoc?.type || null,
          linked_doc_title: linkedDoc?.title || null,
          matter_id: matterContext?.id || null,
          matter_name: matterContext?.name || null,
        },
      });
    } catch {
      return null;
    }
  }, [
    activeSources,
    activeUserDocIds,
    agentMessages,
    decomposedIssues,
    docSections,
    docTemplate,
    docTitle,
    getAuthHeaders,
    kickoffCases,
    kickoffReport,
    kickoffUserDocs,
    researchIntelligence,
    linkedDoc,
    matterContext,
    pinnedSources,
    primaryCourt,
    researchTopic,
    researchType,
    sourceFilter,
    sources,
    sourcesQuery,
    workflowMode,
    workspace?.workspace_id,
    workspaceId,
  ]);

  const openMasterDocument = useCallback(async () => {
    const resolvedWorkspaceId = workspace?.workspace_id || workspaceId;
    if (!workspace?.master_document_id || !resolvedWorkspaceId) return;
    await persistWorkspaceSnapshot(resolvedWorkspaceId);
    const researchSearch = buildWorkspaceSearchString({ workspaceId: resolvedWorkspaceId });
    navigate(`/documents/${workspace.master_document_id}${researchSearch ? `?${researchSearch}` : ''}`, {
      state: {
        fromResearch: true,
        workspaceId: resolvedWorkspaceId,
        researchSearch,
      },
    });
  }, [buildWorkspaceSearchString, navigate, persistWorkspaceSnapshot, workspace?.master_document_id, workspace?.workspace_id, workspaceId]);

  // ── Auto-save refs (capture latest state for unmount cleanup) ──
  const docTitleRef = useRef('Untitled Research Document');
  const docSectionsRef = useRef([]);
  const docTemplateRef = useRef('CUSTOM');
  const isDocSavedRef = useRef(false);
  const agentMessagesRef = useRef([]);
  const activeSourcesRef = useRef([]);
  const researchTopicRef = useRef('');
  const researchTypeRef = useRef('TOPIC');
  const workflowModeRef = useRef('MEMO');
  const primaryCourtRef = useRef('GENERAL');
  const kickoffReportRef = useRef('');
  const researchIntelligenceRef = useRef({});
  const linkedDocRef = useRef(null);
  const matterContextRef = useRef(null);
  useEffect(() => { docTitleRef.current = docTitle; }, [docTitle]);
  useEffect(() => { docSectionsRef.current = docSections; }, [docSections]);
  useEffect(() => { docTemplateRef.current = docTemplate; }, [docTemplate]);
  useEffect(() => { agentMessagesRef.current = agentMessages; }, [agentMessages]);
  useEffect(() => { activeSourcesRef.current = activeSources; }, [activeSources]);
  useEffect(() => { researchTopicRef.current = researchTopic; }, [researchTopic]);
  useEffect(() => { researchTypeRef.current = researchType; }, [researchType]);
  useEffect(() => { workflowModeRef.current = workflowMode; }, [workflowMode]);
  useEffect(() => { primaryCourtRef.current = primaryCourt; }, [primaryCourt]);
  useEffect(() => { kickoffReportRef.current = kickoffReport; }, [kickoffReport]);
  useEffect(() => { researchIntelligenceRef.current = researchIntelligence; }, [researchIntelligence]);
  useEffect(() => { linkedDocRef.current = linkedDoc; }, [linkedDoc]);
  useEffect(() => { matterContextRef.current = matterContext; }, [matterContext]);

  useEffect(() => {
    const activeWorkspaceId = workspace?.workspace_id || workspaceId;
    if (!activeWorkspaceId) return;
    saveResearchWorkspaceDraft(activeWorkspaceId, buildResearchWorkspaceSnapshot({
      researchTopic,
      researchType,
      workflowMode,
      primaryCourt,
      docTitle,
      docTemplate,
      docSections,
      agentMessages,
      activeSources,
      sources,
      pinnedSources,
      sourceFilter,
      sourcesQuery,
      activeUserDocIds,
      kickoffReport,
      kickoffCases,
      kickoffUserDocs,
      decomposedIssues,
      researchIntelligence,
    }));
  }, [
    activeSources,
    activeUserDocIds,
    agentMessages,
    decomposedIssues,
    docSections,
    docTemplate,
    docTitle,
    kickoffCases,
    kickoffReport,
    kickoffUserDocs,
    researchIntelligence,
    pinnedSources,
    primaryCourt,
    researchTopic,
    researchType,
    sourceFilter,
    sources,
    sourcesQuery,
    workflowMode,
    workspace?.workspace_id,
    workspaceId,
  ]);

  // Auto-save document on page navigation away
  useEffect(() => {
    return () => {
      const sections = docSectionsRef.current;
      const title = docTitleRef.current;
      const brief = kickoffReportRef.current;
      const hasContent = sections.some(s => s.content?.trim()) || brief.trim();
      if (!hasContent || isDocSavedRef.current) return;
      const token = localStorage.getItem('session_token');
      const content = [
        brief ? `## Research Brief\n\n${brief}` : '',
        ...sections.map(s => `## ${s.title}\n\n${s.content || ''}`),
      ].filter(Boolean).join('\n\n---\n\n');
      fetch(`${API}/saved`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({
          type: 'RESEARCH',
          title: `[Draft] ${title}`,
          content: content.substring(0, 500),
          agent_type: 'RESEARCH_AGENT',
          tags: ['research-agent', 'draft'],
          research_data: {
            query: researchTopicRef.current || title,
            research_type: researchTypeRef.current,
            workflow_mode: workflowModeRef.current,
            primary_court: primaryCourtRef.current,
            template: docTemplateRef.current,
            sections,
            agent_messages: agentMessagesRef.current,
            active_sources: activeSourcesRef.current,
            kickoff_report: brief,
            linked_doc: linkedDocRef.current,
            matter_context: matterContextRef.current,
            research_intelligence: researchIntelligenceRef.current,
            auto_saved: true,
          },
          workspace: buildSavedItemWorkspace({
            user,
            itemType: 'RESEARCH',
            title: `[Draft] ${title}`,
            content: content.substring(0, 500),
            tags: ['research-agent', 'draft'],
            agentType: 'RESEARCH_AGENT',
            researchData: {
              query: researchTopicRef.current || title,
              research_type: researchTypeRef.current,
              workflow_mode: workflowModeRef.current,
              primary_court: primaryCourtRef.current,
              template: docTemplateRef.current,
              sections,
              agent_messages: agentMessagesRef.current,
              active_sources: activeSourcesRef.current,
              kickoff_report: brief,
              linked_doc: linkedDocRef.current,
              matter_context: matterContextRef.current,
              research_intelligence: researchIntelligenceRef.current,
              auto_saved: true,
            },
          }),
        }),
      }).catch(() => {});
      window.dispatchEvent(new CustomEvent('draft-auto-saved', {
        detail: { type: 'document', title, location: 'Saved Items' },
      }));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Restore state from saved item ────────────────────────────────
  useEffect(() => {
    const savedItemId = searchParams.get('saved_item_id');
    if (!savedItemId) return;
    const restore = async () => {
      try {
        const resp = await fetch(`${API}/saved/${savedItemId}`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        });
        if (!resp.ok) return;
        const item = await resp.json();
        const rd = item.research_data || {};
        restoreResearchState({
          ...rd,
          document_title: item.title ? item.title.replace(/^\[Draft\]\s*/, '') : rd.document_title,
        });
        setWorkspaceStarted(true);
        isDocSavedRef.current = true;
      } catch {}
    };
    restore();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!workspaceId) {
      setWorkspace(null);
      return;
    }
    loadWorkspaceState(workspaceId);
  }, [loadWorkspaceState, workspaceId]);

  useEffect(() => {
    const linkedDocId = searchParams.get('linked_doc_id');
    const linkedDocType = searchParams.get('linked_doc_type');
    const linkedDocTitle = searchParams.get('linked_doc_title');
    if (linkedDocId && linkedDocType) {
      setLinkedDoc({
        id: linkedDocId,
        type: linkedDocType,
        title: linkedDocTitle || 'Linked document',
      });
    }

    const matterId = searchParams.get('matter_id');
    const matterName = searchParams.get('matter_name');
    if (matterId || matterName) {
      setMatterContext({
        id: matterId || matterName,
        name: matterName || matterId,
      });
    }

    const prefillQuery = searchParams.get('prefill_query');
    const prefillType = searchParams.get('prefill_type');
    const focusAuthority = searchParams.get('focus_authority');
    const focusIssue = searchParams.get('focus_issue');
    if (prefillQuery) {
      setResearchTopic(prefillQuery);
      setSourcesQuery(prefillQuery);
    }
    if (prefillType && RESEARCH_TYPES.some(type => type.value === prefillType)) {
      setResearchType(prefillType);
    }
    if (focusAuthority) {
      setLeftTab('sources');
      setSourceFilter('all');
      setSourcesQuery(focusAuthority);
    }
    if (focusIssue) {
      setLeftTab('sources');
      setSourcesQuery(focusIssue);
    }
  }, [searchParams]);

  useEffect(() => {
    if (contextPanel === 'files') {
      setLeftTab('knowledge');
    }
  }, [contextPanel]);

  useEffect(() => {
    const focusAuthority = searchParams.get('focus_authority');
    const focusIssue = searchParams.get('focus_issue');
    if (!focusAuthority && !focusIssue) return;
    const cueKey = `${focusAuthority || ''}::${focusIssue || ''}::${workspace?.workspace_id || workspaceId || ''}`;
    if (focusCueAppliedRef.current === cueKey) return;

    let applied = false;
    if (focusAuthority) {
      const normalizedCue = focusAuthority.toLowerCase();
      const matchingSource = [...activeSources, ...sources].find(source =>
        String(source?.title || '').toLowerCase().includes(normalizedCue)
      );
      if (matchingSource) {
        setExpandedSource(matchingSource.id);
        setLeftTab('sources');
        applied = true;
      }
      setAgentMessages(prev => [
        ...prev,
        {
          role: 'system',
          content: `Focused from Master Doc on authority: ${focusAuthority}`,
          type: 'focus_authority',
        },
      ]);
      applied = true;
    }
    if (focusIssue) {
      setAgentMessages(prev => [
        ...prev,
        {
          role: 'system',
          content: `Focused from Master Doc on issue: ${focusIssue}`,
          type: 'focus_issue',
        },
      ]);
      applied = true;
    }
    if (applied) {
      focusCueAppliedRef.current = cueKey;
    }
  }, [activeSources, searchParams, sources, workspace?.workspace_id, workspaceId]);

  // ── Sort helper: SC first, then HC, then others ──────────────────
  const sortByCourt = arr => [...arr].sort((a, b) => {
    const ds = s => (s.docsource || '').toLowerCase();
    const rank = s => ds(s).includes('supreme court') ? 0 : ds(s).includes('high court') ? 1 : 2;
    return rank(a) - rank(b);
  });

  // ── Core decompose-and-search ─────────────────────────────────────
  const decomposeAndSearch = useCallback(async (topic, contextStr = '', keepExisting = false) => {
    if (!topic) return;
    setSearchingSources(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

      // Step 1: AI decomposition
      let issues = [topic];
      try {
        const dr = await fetch(`${API}/research/decompose`, {
          method: 'POST', headers, credentials: 'include',
          body: JSON.stringify({ topic, context: contextStr }),
        });
        if (dr.ok) {
          const dj = await dr.json();
          if (dj.issues?.length) issues = dj.issues;
        }
      } catch { /* fall back to raw topic */ }
      setDecomposedIssues(issues);

      // Step 2: parallel IK searches
      const searches = await Promise.allSettled(
        issues.map(issue =>
          fetch(`${API}/research/search`, {
            method: 'POST', headers, credentials: 'include',
            body: JSON.stringify({ query: issue }),
          }).then(r => r.ok ? r.json() : { results: [] })
        )
      );

      // Step 3: tag with group, sort SC-first within group
      const incoming = [];
      const seenIds = new Set();
      const nextSummary = {};
      searches.forEach((res, i) => {
        if (res.status !== 'fulfilled') return;
        Object.entries(res.value.source_counts || {}).forEach(([label, count]) => {
          nextSummary[label] = (nextSummary[label] || 0) + count;
        });
        const tagged = sortByCourt((res.value.results || []).map(r => normalizeResearchSource(r, issues[i])));
        tagged.forEach(s => { if (!seenIds.has(s.id)) { seenIds.add(s.id); incoming.push(s); } });
      });
      setSourceSummary(nextSummary);

      setSources(prev => {
        const pinnedIds = new Set(pinnedSources.map(s => s.id));
        const pinnedItems = prev.filter(s => pinnedIds.has(s.id));
        const base = keepExisting ? prev.filter(s => !pinnedIds.has(s.id)) : [];
        const existingIds = new Set([...pinnedItems, ...base].map(s => s.id));
        return [...pinnedItems, ...base, ...incoming.filter(s => !existingIds.has(s.id))];
      });

      if (incoming.length) toast.success(`Found ${incoming.length} sources across ${issues.length} legal issues`);
      else toast.info('No results found');
    } catch {
      toast.error('Search failed');
    } finally {
      setSearchingSources(false);
    }
  }, [pinnedSources]); // eslint-disable-line

  // ── Refresh sources from current chat + doc context ───────────────
  const refreshSources = useCallback(() => {
    const topic = docTitle !== 'Untitled Research Document' ? docTitle : researchTopic || sourcesQuery;
    if (!topic) { toast.info('Set a research topic first'); return; }
    const recentChat = agentMessages
      .filter(m => (m.role === 'user' || m.role === 'agent') && m.content)
      .slice(-6)
      .map(m => `${m.role === 'user' ? 'User' : 'Agent'}: ${m.content.slice(0, 250)}`)
      .join('\n');
    const docContent = docSections
      .filter(s => s.content)
      .map(s => `${s.title}: ${s.content.slice(0, 200)}`)
      .join('\n');
    const context = [recentChat, docContent].filter(Boolean).join('\n\n');
    decomposeAndSearch(topic, context, false);
  }, [docTitle, researchTopic, sourcesQuery, agentMessages, docSections, decomposeAndSearch]);

  const toggleActive = src =>
    setActiveSources(prev =>
      prev.some(s => s.id === src.id) ? prev.filter(s => s.id !== src.id) : [...prev, src]
    );

  const togglePin = src =>
    setPinnedSources(prev =>
      prev.some(s => s.id === src.id) ? prev.filter(s => s.id !== src.id) : [...prev, src]
    );

  const removeSource = id => {
    setSources(prev => prev.filter(s => s.id !== id));
    setActiveSources(prev => prev.filter(s => s.id !== id));
    setPinnedSources(prev => prev.filter(s => s.id !== id));
  };

  // ── Knowledge base ────────────────────────────────────────────
  const loadUserDocs = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const token = localStorage.getItem('session_token');
      const resp = await fetch(`${API}/uploads`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
      });
      if (resp.ok) setUserDocs(await resp.json());
    } catch {
      toast.error('Failed to load knowledge base');
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  useEffect(() => { loadUserDocs(); }, [loadUserDocs]);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    const allowed = new Set(['.pdf', '.docx', '.txt', '.doc', '.jpg', '.jpeg', '.png', '.tiff', '.webp']);
    const valid = Array.from(files).filter(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      if (!allowed.has(ext)) { toast.error(`${f.name}: unsupported type`); return false; }
      if (f.size > 50 * 1024 * 1024) { toast.error(`${f.name}: exceeds 50 MB limit`); return false; }
      return true;
    });
    if (!valid.length) return;

    setUploadingDocs(true);
    let totalUploaded = 0;
    const BATCH = 50;
    try {
      const token = localStorage.getItem('session_token');
      for (let i = 0; i < valid.length; i += BATCH) {
        const chunk = valid.slice(i, i + BATCH);
        const formData = new FormData();
        chunk.forEach(f => formData.append('files', f));
        const resp = await fetch(`${API}/uploads/batch`, {
          method: 'POST',
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          credentials: 'include',
          body: formData,
        });
        if (resp.ok) {
          const result = await resp.json();
          totalUploaded += result.uploaded?.length || 0;
          if (result.errors?.length) {
            result.errors.slice(0, 3).forEach(e => toast.error(`${e.filename}: ${e.error}`));
          }
        } else {
          toast.error(`Batch upload failed (files ${i + 1}–${i + chunk.length})`);
        }
      }
      if (totalUploaded > 0) {
        toast.success(`${totalUploaded} file${totalUploaded !== 1 ? 's' : ''} added to context`);
        await loadUserDocs();
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploadingDocs(false);
    }
  };

  const deleteUserDoc = async (uploadId) => {
    try {
      const token = localStorage.getItem('session_token');
      const resp = await fetch(`${API}/uploads/${uploadId}`, {
        method: 'DELETE',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
      });
      if (resp.ok) {
        setUserDocs(prev => prev.filter(d => d.upload_id !== uploadId));
        setActiveUserDocIds(prev => prev.filter(id => id !== uploadId));
        toast.success('Document moved to trash');
      }
    } catch {
      toast.error('Failed to move document to trash');
    }
  };

  const toggleUserDoc = (uploadId) =>
    setActiveUserDocIds(prev =>
      prev.includes(uploadId) ? prev.filter(id => id !== uploadId) : [...prev, uploadId]
    );

  // ── Chat file attachment ───────────────────────────────────────
  const handleChatFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const allowed = new Set(['.pdf', '.docx', '.txt', '.doc']);
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowed.has(ext)) { toast.error('Unsupported type. Upload PDF, DOCX, or TXT.'); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error('File too large. Maximum 15MB.'); return; }
    setChatPendingFile(file);
  };

  const uploadChatFile = async () => {
    if (!chatPendingFile) return;
    setChatUploadingFile(true);
    try {
      const token = localStorage.getItem('session_token');
      const formData = new FormData();
      formData.append('file', chatPendingFile);
      const resp = await fetch(`${API}/uploads`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: formData,
      });
      if (resp.ok) {
        const doc = await resp.json();
        setActiveUserDocIds(prev => [...prev, doc.upload_id]);
        setUserDocs(prev => [doc, ...prev.filter(d => d.upload_id !== doc.upload_id)]);
        toast.success(`"${chatPendingFile.name}" added to context`);
        setChatPendingFile(null);
        return doc.upload_id;
      } else {
        toast.error('Upload failed');
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setChatUploadingFile(false);
    }
    return null;
  };

  // ── Template selection ────────────────────────────────────────
  const applyTemplate = key => {
    setDocTemplate(key);
    setDocSections(createTemplateSections(key));
    const matchingFlow = WORKSPACE_FLOWS.find(flow => flow.template === key);
    if (matchingFlow) setWorkflowMode(matchingFlow.value);
    toast.success(`Applied ${TEMPLATES[key].label} template`);
  };

  const switchWorkflowMode = (nextMode) => {
    const matchingFlow = WORKSPACE_FLOWS.find(flow => flow.value === nextMode);
    if (!matchingFlow) return;
    setWorkflowMode(nextMode);

    const hasMeaningfulDraft = docSections.some(section => section.content?.trim());
    if (!hasMeaningfulDraft) {
      setDocTemplate(matchingFlow.template);
      setDocSections(createTemplateSections(matchingFlow.template));
    }

    const currentKnownTitle = [
      buildDocumentTitle(researchTopic, workflowMode),
      buildDocumentTitle(researchTopic, nextMode),
      'Untitled Research Document',
    ];
    if (!docTitle || currentKnownTitle.includes(docTitle)) {
      setDocTitle(buildDocumentTitle(researchTopic, nextMode));
    }

    toast.success(`${matchingFlow.label} is now the active research mode`);
  };

  // ── Section management ────────────────────────────────────────
  const addSection = () => {
    const id = `section_${Date.now()}`;
    setDocSections(prev => [...prev, { id, title: 'New Section', content: '', placeholder: 'Start writing...' }]);
    setTimeout(() => sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const updateSection = (id, field, value) =>
    setDocSections(prev => prev.map(s => (s.id === id ? { ...s, [field]: value } : s)));

  const removeSection = id => setDocSections(prev => prev.filter(s => s.id !== id));

  const moveSection = (id, dir) => {
    setDocSections(prev => {
      const idx = prev.findIndex(s => s.id === id);
      const swap = dir === 'up' ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  };

  // ── Generate section via agent ────────────────────────────────
  const generateSection = async sectionId => {
    const section = docSections.find(s => s.id === sectionId);
    if (!section) return;
    setGeneratingSection(sectionId);
    const prompt = `Write the "${section.title}" section for my ${TEMPLATES[docTemplate]?.label || 'document'} titled "${docTitle}".
Use the active sources for citations where relevant.
Return ONLY the section content (no heading, no preamble). Use markdown for formatting.`;
    await _callAgent(prompt, fullText => {
      const marker = fullText.match(/\[SECTION:[^\]]*\]([\s\S]*?)\[\/SECTION\]/);
      updateSection(sectionId, 'content', (marker ? marker[1] : fullText).trim());
    });
    setGeneratingSection(null);
  };

  // ── Core agent call ───────────────────────────────────────────
  const _callAgent = async (message, onComplete = null) => {
    if (agentAbortRef.current) agentAbortRef.current.abort();
    const controller = new AbortController();
    agentAbortRef.current = controller;
    setAgentStreaming(true);

    const msgId = Date.now();
    if (!onComplete) {
      setAgentMessages(prev => [
        ...prev,
        { role: 'agent', content: '', id: msgId, streaming: true },
      ]);
    }

    try {
      const activeWorkspace = await ensureWorkspaceForResearch(message || docTitle || researchTopic);
      const token = localStorage.getItem('session_token');
      const resp = await fetch(`${API}/research/agent/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          message,
          document_title: docTitle,
          document_sections: docSections,
          active_sources: activeSources,
          sources,
          pinned_sources: pinnedSources,
          source_filter: sourceFilter,
          sources_query: sourcesQuery,
          active_user_doc_ids: activeUserDocIds,
          history: agentMessages
            .filter(m => !m.streaming && m.type !== 'intro')
            .slice(-10)
            .map(m => ({ role: m.role === 'agent' ? 'assistant' : 'user', content: m.content })),
          template_type: docTemplate,
          research_type: researchType,
          workflow_mode: workflowMode,
          primary_court: primaryCourt,
          workspace_id: activeWorkspace?.workspace_id || workspace?.workspace_id || workspaceId || null,
          linked_doc_id: linkedDoc?.id || null,
          linked_doc_type: linkedDoc?.type || null,
          linked_doc_title: linkedDoc?.title || null,
          matter_id: matterContext?.id || null,
          matter_name: matterContext?.name || null,
        }),
        signal: controller.signal,
      });

      if (!resp.ok) throw new Error('Agent request failed');

      let full = '';

      await readEventStream(resp, async (ev) => {
        if (ev.type === 'text') {
          full += ev.text;
          if (!onComplete)
            setAgentMessages(prev =>
              prev.map(m => (m.id === msgId ? { ...m, content: full } : m))
            );
        }
        if (ev.type === 'new_sources') {
          const incoming = sortByCourt((ev.sources || []).map(s => normalizeResearchSource(s, ev.query ? `Agent: ${ev.query}` : 'Agent Search')));
          setSources(prev => {
            const existing = new Set(prev.map(s => s.id));
            return [...prev, ...incoming.filter(s => !existing.has(s.id))];
          });
          setAgentMessages(prev => [
            ...prev,
            {
              role: 'system',
              content: `Added ${incoming.length} new source${incoming.length !== 1 ? 's' : ''} to the Sources panel.`,
              type: 'sources_added',
            },
          ]);
        }
        if (ev.type === 'context_debug') {
          setContextDebug(ev.items || []);
        }
        if (ev.type === 'done') {
          const resolvedWorkspaceId = activeWorkspace?.workspace_id || workspace?.workspace_id || workspaceId;
          if (ev.research_intelligence && typeof ev.research_intelligence === 'object') {
            setResearchIntelligence(ev.research_intelligence);
            if (resolvedWorkspaceId) {
              saveResearchWorkspaceDraft(resolvedWorkspaceId, buildResearchWorkspaceSnapshot({
                researchTopic,
                researchType,
                workflowMode,
                primaryCourt,
                docTitle,
                docTemplate,
                docSections,
                agentMessages,
                activeSources: combinedPromptSources,
                sources,
                pinnedSources,
                sourceFilter,
                sourcesQuery,
                activeUserDocIds,
                kickoffReport,
                kickoffCases,
                kickoffUserDocs,
                decomposedIssues,
                researchIntelligence: ev.research_intelligence,
              }));
            }
          }
          if (!onComplete)
            setAgentMessages(prev =>
              prev.map(m => (m.id === msgId ? { ...m, streaming: false } : m))
            );
          else onComplete(full);
          if (resolvedWorkspaceId && !(ev.research_intelligence && typeof ev.research_intelligence === 'object')) {
            loadWorkspaceState(resolvedWorkspaceId);
          }
        }
        if (ev.type === 'error') {
          const errorMessage = ev.error || 'Agent error — please try again';
          toast.error(errorMessage);
          if (!onComplete) {
            setAgentMessages(prev =>
              prev.map(m => (
                m.id === msgId
                  ? { ...m, content: m.content || 'Agent error — please try again.', streaming: false }
                  : m
              ))
            );
          }
        }
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Agent error — please try again');
        if (!onComplete)
          setAgentMessages(prev => prev.filter(m => m.id !== msgId));
      }
    } finally {
      setAgentStreaming(false);
      agentAbortRef.current = null;
    }
  };

  const sendAgentMessage = async () => {
    const msg = agentInput.trim();
    if (!msg || agentStreaming) return;
    // Upload any pending chat file first
    if (chatPendingFile && !chatUploadingFile) await uploadChatFile();
    setAgentMessages(prev => [...prev, { role: 'user', content: msg }]);
    setAgentInput('');
    await _callAgent(msg);
  };

  const mergeKickoffSources = (incoming, groupLabel) => {
    const normalized = sortByCourt((incoming || []).map(source => normalizeResearchSource(source, groupLabel)));
    if (!normalized.length) return [];
    setSources(prev => mergeSourceLists(prev, normalized));
    return normalized;
  };

  const saveDocToKnowledgeBase = async () => {
    const hasContent = docSections.some(s => s.content?.trim());
    if (!hasContent) { toast.error('Add content to the document first'); return; }
    const fullContent = `# ${docTitle}\n\n` + docSections
      .filter(s => s.content?.trim())
      .map(s => `## ${s.title}\n\n${s.content}`)
      .join('\n\n');
    const blob = new Blob([fullContent], { type: 'text/plain' });
    const safeName = (docTitle || 'research').replace(/[^a-zA-Z0-9\s-]/g, '').trim().replace(/\s+/g, '_') || 'research';
    const file = new File([blob], `${safeName}.txt`, { type: 'text/plain' });
    const formData = new FormData();
    formData.append('file', file);
    if (matterContext?.id) formData.append('matter_id', matterContext.id);
    const token = localStorage.getItem('session_token');
    try {
      const resp = await fetch(`${API}/knowledge/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
        body: formData,
      });
      if (resp.ok) {
        toast.success('Document saved to Knowledge Base — it will be indexed shortly');
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save to Knowledge Base');
      }
    } catch {
      toast.error('Failed to save to Knowledge Base');
    }
  };

  const insertKickoffIntoDocument = () => {
    if (!kickoffReport.trim()) return;
    const nextSection = {
      id: 'kickoff_brief',
      title: 'Research Brief',
      content: kickoffReport,
      placeholder: 'Initial verified research brief...',
    };
    setDocSections(prev => {
      const existingIndex = prev.findIndex(section => section.id === 'kickoff_brief');
      if (existingIndex >= 0) {
        return prev.map(section => (section.id === 'kickoff_brief' ? { ...section, content: kickoffReport } : section));
      }
      return [nextSection, ...prev];
    });
    toast.success('Research brief inserted into the document');
  };

  const runKickoffResearch = async (topic, nextResearchType = researchType, targetWorkspaceId = null) => {
    if (!topic) return;
    if (kickoffAbortRef.current) kickoffAbortRef.current.abort();

    setKickoffResearching(true);
    setKickoffStatus('Collecting verified authorities...');
    setKickoffReport('');
    setKickoffCases([]);
    setKickoffUserDocs([]);

    const controller = new AbortController();
    kickoffAbortRef.current = controller;

    try {
      const token = localStorage.getItem('session_token');
      const resp = await fetch(`${API}/research/deep`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          query: topic,
          research_type: nextResearchType,
          workspace_id: targetWorkspaceId || workspace?.workspace_id || workspaceId || null,
          linked_doc_id: linkedDoc?.id || null,
          linked_doc_type: linkedDoc?.type || null,
          linked_doc_title: linkedDoc?.title || null,
          matter_id: matterContext?.id || null,
          matter_name: matterContext?.name || null,
        }),
        signal: controller.signal,
      });

      if (!resp.ok) throw new Error('Kickoff research failed');

      let report = '';

      await readEventStream(resp, async ev => {
        if (ev.type === 'status') setKickoffStatus(ev.message || '');
        if (ev.type === 'cases_found') {
          const normalized = mergeKickoffSources((ev.cases || []).map(source => normalizeResearchSource(source, 'Verified brief')), 'Verified brief');
          setKickoffCases(normalized);
          setPinnedSources(prev => (prev.length ? prev : normalized.slice(0, 3)));
          setActiveSources(prev => (prev.length ? prev : normalized.slice(0, 4)));
        }
        if (ev.type === 'user_docs') setKickoffUserDocs(ev.docs || []);
        if (ev.type === 'context_debug') setContextDebug(ev.items || []);
        if (ev.type === 'text') {
          report += ev.text;
          setKickoffReport(report);
        }
        if (ev.type === 'done') {
          const resolvedWorkspaceId = targetWorkspaceId || workspace?.workspace_id || workspaceId;
          const normalized = mergeKickoffSources((ev.verified_cases || []).map(source => normalizeResearchSource(source, 'Verified brief')), 'Verified brief');
          if (normalized.length) setKickoffCases(normalized);
          if (ev.user_docs?.length) setKickoffUserDocs(ev.user_docs);
          if (ev.source_counts) setSourceSummary(ev.source_counts);
          if (ev.context_used) setContextDebug(ev.context_used);
          if (ev.research_intelligence && typeof ev.research_intelligence === 'object') {
            setResearchIntelligence(ev.research_intelligence);
            if (resolvedWorkspaceId) {
              saveResearchWorkspaceDraft(resolvedWorkspaceId, buildResearchWorkspaceSnapshot({
                researchTopic: topic,
                researchType: nextResearchType,
                workflowMode,
                primaryCourt,
                docTitle,
                docTemplate,
                docSections,
                agentMessages,
                activeSources: activeSources.length ? activeSources : normalized.slice(0, 4),
                sources: normalized.length ? mergeSourceLists(sources, normalized) : sources,
                pinnedSources: pinnedSources.length ? pinnedSources : normalized.slice(0, 3),
                sourceFilter,
                sourcesQuery: topic,
                activeUserDocIds,
                kickoffReport: report,
                kickoffCases: normalized.length ? normalized : kickoffCases,
                kickoffUserDocs: ev.user_docs?.length ? ev.user_docs : kickoffUserDocs,
                decomposedIssues,
                researchIntelligence: ev.research_intelligence,
              }));
            }
          }
          setKickoffStatus('');
          if (resolvedWorkspaceId && !(ev.research_intelligence && typeof ev.research_intelligence === 'object')) {
            loadWorkspaceState(resolvedWorkspaceId);
          }
        }
        if (ev.type === 'error') toast.error(ev.error || 'Kickoff research failed');
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Kickoff research failed. Please try again.');
        setKickoffStatus('');
      }
    } finally {
      setKickoffResearching(false);
      kickoffAbortRef.current = null;
    }
  };

  // ── Start research ────────────────────────────────────────────
  const startResearch = async (options = {}) => {
    const topic = (options.topic || researchTopic).trim();
    const nextResearchType = options.researchType || researchType;
    const nextWorkflowMode = options.workflowMode || workflowMode;
    if (!topic) return;
    const activeWorkspace = await ensureWorkspaceForResearch(topic);

    const chosenFlow = WORKSPACE_FLOWS.find(flow => flow.value === nextWorkflowMode) || WORKSPACE_FLOWS[0];
    const nextTemplate = chosenFlow.template;
    const nextTitle = buildDocumentTitle(topic, nextWorkflowMode);
    const shouldResetWorkspace = options.resetWorkspace ?? !workspaceStarted;

    if (shouldResetWorkspace) {
      setSources([]);
      setPinnedSources([]);
      setActiveSources([]);
      setDecomposedIssues([]);
      setContextDebug([]);
      setSourceSummary({});
      setResearchIntelligence({});
      setKickoffReport('');
      setKickoffCases([]);
      setKickoffUserDocs([]);
    }

    isDocSavedRef.current = false;
    setWorkspaceStarted(true);
    setResearchTopic(topic);
    setResearchType(nextResearchType);
    setWorkflowMode(nextWorkflowMode);
    setSourcesQuery(topic);

    if (shouldResetWorkspace) {
      setDocTemplate(nextTemplate);
      setDocSections(createTemplateSections(nextTemplate));
      setDocTitle(nextTitle);
    } else {
      setDocTitle(prev => (prev === 'Untitled Research Document' ? nextTitle : prev));
    }

    decomposeAndSearch(topic);
    runKickoffResearch(topic, nextResearchType, activeWorkspace?.workspace_id || workspace?.workspace_id || workspaceId);

    const contextBits = [
      `Research file opened for "${topic}"`,
      `${chosenFlow.label} selected`,
      RESEARCH_TYPES.find(type => type.value === nextResearchType)?.label,
      COURT_OPTIONS.find(option => option.value === primaryCourt)?.label,
      linkedDoc?.title ? `linked to ${linkedDoc.title}` : '',
      matterContext?.name ? `matter: ${matterContext.name}` : '',
    ].filter(Boolean);

    setAgentMessages(prev => [
      ...prev,
      { role: 'system', content: contextBits.join(' · '), type: 'topic_set' },
    ]);
  };

  // ── Save document ─────────────────────────────────────────────
  const saveDocument = async () => {
    setSavingDoc(true);
    try {
      const content = [
        kickoffReport ? `## Research Brief\n\n${kickoffReport}` : '',
        ...docSections.map(s => `## ${s.title}\n\n${s.content || ''}`),
      ].filter(Boolean).join('\n\n---\n\n');
      const resp = await fetch(`${API}/saved`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          type: 'RESEARCH',
          title: docTitle,
          content: content.substring(0, 500),
          agent_type: 'RESEARCH_AGENT',
          tags: ['research-agent'],
          research_data: {
            query: researchTopic || docTitle,
            research_type: researchType,
            workflow_mode: workflowMode,
            primary_court: primaryCourt,
            template: docTemplate,
            sections: docSections,
            agent_messages: agentMessages,
            active_sources: activeSources,
            source_count: activeSources.length,
            kickoff_report: kickoffReport,
            kickoff_cases: kickoffCases,
            kickoff_user_docs: kickoffUserDocs,
            linked_doc: linkedDoc,
            matter_context: matterContext,
            research_intelligence: researchIntelligence,
          },
          workspace: buildSavedItemWorkspace({
            user,
            itemType: 'RESEARCH',
            title: docTitle,
            content: content.substring(0, 500),
            tags: ['research-agent'],
            agentType: 'RESEARCH_AGENT',
            researchData: {
              query: researchTopic || docTitle,
              research_type: researchType,
              workflow_mode: workflowMode,
              primary_court: primaryCourt,
              template: docTemplate,
              sections: docSections,
              agent_messages: agentMessages,
              active_sources: activeSources,
              source_count: activeSources.length,
              kickoff_report: kickoffReport,
              kickoff_cases: kickoffCases,
              kickoff_user_docs: kickoffUserDocs,
              linked_doc: linkedDoc,
              matter_context: matterContext,
              research_intelligence: researchIntelligence,
            },
          }),
        }),
      });
      if (resp.ok) {
        isDocSavedRef.current = true;
        clearResearchWorkspaceDraft(workspace?.workspace_id || workspaceId);
        toast.success('Saved to your Saved Items');
      } else throw new Error();
    } catch {
      toast.error('Failed to save document');
    } finally {
      setSavingDoc(false);
    }
  };

  // ── Export markdown ───────────────────────────────────────────
  const exportMarkdown = () => {
    const lines = [
      `# ${docTitle}`,
      '',
      activeSources.length > 0
        ? `**Sources:** ${activeSources.map(s => `[${s.title}](${s.url})`).join(' · ')}`
        : '',
      '',
      kickoffReport ? `## Research Brief\n\n${kickoffReport}\n` : '',
      ...docSections.map(s => `## ${s.title}\n\n${s.content || ''}`),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${docTitle.replace(/\s+/g, '-').toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as Markdown');
  };

  // ── Copy document ─────────────────────────────────────────────
  const copyDocument = () => {
    const text = [
      kickoffReport ? `Research Brief\n\n${kickoffReport}` : '',
      ...docSections.map(s => `${s.title}\n\n${s.content || ''}`),
    ].filter(Boolean).join('\n\n---\n\n');
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  useEffect(() => {
    const savedItemId = searchParams.get('saved_item_id');
    const prefillQuery = searchParams.get('prefill_query');
    if (savedItemId || !prefillQuery || autoStartDoneRef.current) return;
    autoStartDoneRef.current = true;
    startResearch({ topic: prefillQuery, researchType: searchParams.get('prefill_type') || researchType });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, researchType]);

  // ── Derived values ────────────────────────────────────────────
  const wordCount = docSections.reduce(
    (acc, s) => acc + (s.content?.split(/\s+/).filter(Boolean).length || 0),
    0
  );
  const currentFlow = WORKSPACE_FLOWS.find(flow => flow.value === workflowMode) || WORKSPACE_FLOWS[0];
  const currentResearchType = RESEARCH_TYPES.find(type => type.value === researchType) || RESEARCH_TYPES[0];
  const currentCourt = COURT_OPTIONS.find(option => option.value === primaryCourt) || COURT_OPTIONS[0];
  const pinnedIds = new Set(pinnedSources.map(s => s.id));
  const activeIds = new Set(activeSources.map(s => s.id));
  const jollyContextCount = contextDebug.filter(item => item?.trust_label === 'Jolly-Derived Matter Memory').length;
  const matterFileContextCount = contextDebug.filter(item => item?.trust_label === 'Matter File').length;
  const trustSummary = buildTrustSummary(dedupeSourceList([...sources, ...contextDebug]));
  const trustedSourceCount = trustSummary
    .filter(item => item.label === 'Verified Authority' || item.label === 'Verified Government Source')
    .reduce((sum, item) => sum + item.count, 0);
  const chatReturnRecommendation = buildAgentHandoffRecommendation({
    currentAgent: 'RESEARCH_AGENT',
    workspace,
    userPrompt: researchTopic,
    assistantResponse: kickoffReport,
  });
  const deskModel = buildResearchDeskModel({
    researchTopic,
    workflowMode,
    primaryCourt,
    researchType,
    sources,
    activeSources,
    pinnedSources,
    decomposedIssues,
    kickoffReport,
    contextDebug,
    researchIntelligence,
  });
  const workflowDetails = getWorkflowModeDetails(workflowMode);
  const quickActionGroups = deskModel.quickActions.reduce((groups, action) => {
    const key = action.group || 'Draft';
    if (!groups[key]) groups[key] = [];
    groups[key].push(action);
    return groups;
  }, {});

  const filteredSources = sources.filter(s => {
    if (sourceFilter === 'all') return true;
    const ds = (s.docsource || '').toLowerCase();
    if (sourceFilter === 'sc') return ds.includes('supreme court');
    if (sourceFilter === 'hc') return ds.includes('high court');
    if (sourceFilter === 'tribunal') return ds.includes('tribunal') || ds.includes('commission');
    if (sourceFilter === 'gov') return (s.source_family || '').includes('government') || s.trust_label === 'Verified Government Source';
    return true;
  });
  const resolvedMatterContext = matterContext || workspace?.matter_context || null;
  const contextMatterId = resolvedMatterContext?.id || '';
  const contextMatterName = resolvedMatterContext?.name || contextMatterId || '';
  const overviewRoute = contextMatterId ? `/war-room/${contextMatterId}` : '/war-room';
  const workspaceContextSearch = buildWorkspaceSearchString({
    workspaceId: workspace?.workspace_id || workspaceId,
    matterContext: resolvedMatterContext,
  });
  const draftRoute = workspace?.master_document_id
    ? `/documents/${workspace.master_document_id}${workspaceContextSearch ? `?${workspaceContextSearch}` : ''}`
    : withQueryParams('/documents', {
      matter_id: contextMatterId,
      matter_name: contextMatterName,
    });
  const filesRoute = withQueryParams(
    `/research${workspaceContextSearch ? `?${workspaceContextSearch}` : ''}`,
    { context_panel: 'files' },
  );
  const jollyRoute = withQueryParams('/jolly', {
    matter_id: contextMatterId,
    matter_name: contextMatterName,
  });
  const reviewRoute = contextMatterId ? `/war-room/${contextMatterId}?view=agents` : '/war-room?view=agents';
  const matterContextItems = [
    { key: 'overview', onClick: () => navigate(overviewRoute) },
    { key: 'draft', badge: workspace?.master_document_id ? 'Live' : '', onClick: () => navigate(draftRoute) },
    { key: 'files', badge: activeUserDocIds.length || userDocs.length ? String(activeUserDocIds.length || userDocs.length) : '', onClick: () => navigate(filesRoute) },
    { key: 'jolly', badge: jollyContextCount ? String(jollyContextCount) : '', onClick: () => navigate(jollyRoute) },
    { key: 'review', onClick: () => navigate(reviewRoute) },
  ];
  const matterContextActiveKey = contextPanel === 'files' || leftTab === 'knowledge' ? 'files' : 'overview';

  const unpinnedSources = filteredSources.filter(s => !pinnedIds.has(s.id));

  // Group unpinned sources by their `group` field (preserving insertion order)
  const sourceGroups = (() => {
    const map = new Map();
    unpinnedSources.forEach(s => {
      const g = s.group || 'Results';
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(s);
    });
    return Array.from(map.entries()).map(([label, srcs]) => ({ label, srcs }));
  })();

  return (
    <div style={{ display: 'contents' }}>

      <main
        data-testid="research-page"
        className="app-main research-agent-shell research-agent-main flex min-h-[calc(100dvh-3.5rem)] flex-col overflow-visible md:min-h-screen md:overflow-hidden"
      >

        {/* ── Top header bar ─────────────────────────────────────── */}
        <div className="research-agent-header" style={{
          padding: '10px 20px',
          borderBottom: '1px solid var(--workspace-surface-border)',
          background: 'var(--workspace-surface-bg)',
          display: 'flex', alignItems: 'center', gap: '12px',
          flexShrink: 0, minHeight: '52px', flexWrap: 'wrap',
        }}>
          {/* Brand */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, minWidth: '185px' }}>
            <div style={{
              width: '28px', height: '28px', borderRadius: '8px',
              background: 'var(--workspace-canvas-card-bg)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid color-mix(in srgb, var(--accent-primary) 18%, var(--workspace-surface-border))',
              boxShadow: 'var(--workspace-surface-shadow-soft)',
            }}>
              <Scale size={14} style={{ color: 'var(--accent-primary)' }} />
            </div>
            <div>
              <p style={{
                fontFamily: 'var(--font-display)',
                color: 'var(--text-primary)', fontSize: '15px', fontWeight: 600, margin: 0,
              }}>
                Files
              </p>
              <p style={{
                fontSize: '10px',
                color: 'var(--text-muted)',
                fontFamily: 'var(--font-ui)',
                margin: '1px 0 0',
              }}>
                Authorities, uploads, and drafting support inside the matter
              </p>
            </div>
          </div>

          {/* Status bar */}
          <div className="flex w-full items-center gap-3 overflow-x-auto pb-1 md:ml-auto md:w-auto md:flex-wrap md:justify-end">
            {showBackToAina ? (
              <HeaderLinkButton
                icon={<MessageSquare size={12} />}
                label="Back to Review"
                onClick={() => navigate(ainaReturnPath)}
              />
            ) : null}
            <FeatureHistoryMenu feature="research" matterId={matterContext?.id || ''} workspaceId={workspace?.workspace_id || workspaceId || ''} label="Recent Research" />
            {workspace && (
              <>
                <StatusPill icon={MessageSquare} label={workspace.activity_type?.replace(/_/g, ' ') || 'Workspace'} active />
                <HeaderLinkButton
                  icon={<MessageSquare size={12} />}
                  label={chatReturnRecommendation?.target === 'CHAT_AGENT' ? 'Draft In Chat' : 'Chat Agent'}
                  onClick={() => navigate(`/chat?${buildWorkspaceSearchString({ workspaceId: workspace.workspace_id })}`)}
                />
                {workspace.master_document_id && (
                  <HeaderLinkButton
                    icon={<FileText size={12} />}
                    label="Master Doc"
                    onClick={openMasterDocument}
                  />
                )}
              </>
            )}
            <StatusPill icon={Globe} label={`${activeSources.length || sources.length} sources`} active={(activeSources.length || sources.length) > 0} />
            <StatusPill icon={Database} label={`${activeUserDocIds.length || userDocs.length} docs`} active={(activeUserDocIds.length || userDocs.length) > 0} />
            <StatusPill icon={Mic} label={`${jollyContextCount} Jolly`} active={jollyContextCount > 0} />
            <StatusPill icon={FileText} label={`${wordCount} words`} />
            <HeaderLinkButton
              icon={<Bot size={12} />}
              label={showDeskCounsel ? 'Hide Desk Counsel' : 'Desk Counsel'}
              onClick={() => setShowDeskCounsel((current) => !current)}
            />
          </div>
        </div>

        {(matterContext?.id || workspace?.matter_context?.id) && (workspace?.workspace_id || workspaceId) ? (
          <div className="px-3 pb-3 md:px-[18px] md:pb-3">
            <CollaborationStrip
              matterId={matterContext?.id || workspace?.matter_context?.id || ''}
              targetType="research_workspace"
              targetId={workspace?.workspace_id || workspaceId || ''}
              targetLabel={workspace?.title || researchTopic || 'Research workspace'}
              fallbackReviewState="new"
              compact
            />
          </div>
        ) : null}

        {contextMatterId ? (
          <div className="px-3 pb-3 md:px-[18px] md:pb-3">
            <MatterWorkspaceContext
              matterName={contextMatterName}
              matterId={contextMatterId}
              workspaceId={workspace?.workspace_id || workspaceId || ''}
              objective={researchTopic || kickoffReport || 'Ground the live file in authorities, uploaded record, and drafting support.'}
              items={matterContextItems}
              activeKey={matterContextActiveKey}
              compact
              caption="Matter context"
              testId="matter-workspace-context"
            />
          </div>
        ) : null}

        {/* ── Three-panel body ───────────────────────────────────── */}
        <div className="flex flex-1 flex-col overflow-visible md:flex-row md:overflow-hidden">

          {/* ══ LEFT: Sources + Knowledge Base ════════════════════ */}
          <div className="research-agent-panel research-agent-panel--left max-h-[45vh] w-full md:max-h-none" style={{
            width: isMobile ? undefined : leftPanelWidth, flexShrink: 0,
            background: 'var(--workspace-rail-bg)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            position: 'relative',
            borderRight: isMobile ? 'none' : '1px solid var(--workspace-surface-border)',
            borderBottom: isMobile ? '1px solid var(--workspace-surface-border)' : 'none',
            boxShadow: 'var(--workspace-surface-shadow-soft)',
          }}>
            {/* Tab switcher */}
            <div style={{
              display: 'flex', borderBottom: '1px solid var(--border-subtle)',
              flexShrink: 0,
            }}>
              {[
                { k: 'sources', label: 'Sources', icon: Globe },
                { k: 'knowledge', label: 'Knowledge Base', icon: Database },
              ].map(({ k, label, icon: Icon }) => (
                <button
                  key={k}
                  onClick={() => setLeftTab(k)}
                  style={{
                    flex: 1, padding: '9px 6px', border: 'none', cursor: 'pointer',
                    background: leftTab === k ? 'var(--bg-elevated)' : 'transparent',
                    color: leftTab === k ? 'var(--accent-primary)' : 'var(--text-muted)',
                    fontSize: '11px', fontWeight: leftTab === k ? 600 : 400,
                    fontFamily: 'var(--font-ui)',
                    borderBottom: leftTab === k ? '2px solid var(--accent-primary)' : '2px solid transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                    transition: 'all 0.12s',
                  }}
                >
                  <Icon size={11} />
                  {label}
                  {k === 'knowledge' && userDocs.length > 0 && (
                    <span style={{
                      fontSize: '9px', padding: '1px 5px', borderRadius: '8px',
                      background: activeUserDocIds.length > 0 ? 'var(--accent-glow)' : 'var(--bg-elevated)',
                      color: activeUserDocIds.length > 0 ? 'var(--accent-primary)' : 'var(--text-muted)',
                      border: '1px solid var(--border-subtle)',
                    }}>
                      {userDocs.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* ── Sources tab ── */}
            {leftTab === 'sources' && (
              <>
                <div style={{ padding: '10px 10px 8px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
                  {/* Header row */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{
                      fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '0.1em', color: 'var(--text-muted)',
                      fontFamily: 'var(--font-ui)',
                    }}>
                      Authority Stack
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {activeSources.length > 0 && (
                        <span style={{
                          fontSize: '10px', padding: '2px 7px', borderRadius: '10px',
                          background: 'var(--accent-glow)', color: 'var(--accent-primary)',
                          border: '1px solid rgba(46,204,90,0.25)',
                          fontFamily: 'var(--font-ui)',
                        }}>
                          {activeSources.length} active
                        </span>
                      )}
                      {/* Refresh button */}
                      <button
                        onClick={refreshSources}
                        disabled={searchingSources}
                        title="Refresh sources based on current chat & document"
                        style={{
                          display: 'flex', alignItems: 'center', gap: '4px',
                          padding: '3px 8px', borderRadius: '6px', border: '1px solid var(--border-default)',
                          background: 'var(--bg-elevated)',
                          color: searchingSources ? 'var(--text-muted)' : 'var(--accent-primary)',
                          fontSize: '10px', cursor: searchingSources ? 'wait' : 'pointer',
                          fontFamily: 'var(--font-ui)',
                        }}
                      >
                        <RefreshCw size={10} style={{ animation: searchingSources ? 'spin 1s linear infinite' : 'none' }} />
                        Refresh
                      </button>
                    </div>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: '6px',
                    marginBottom: '8px',
                  }}>
                    {deskModel.authorityMap.buckets.slice(0, 4).map(bucket => (
                      <div
                        key={bucket.key}
                        style={{
                          borderRadius: '8px',
                          border: '1px solid var(--border-subtle)',
                          background: 'var(--bg-base)',
                          padding: '7px 8px',
                        }}
                      >
                        <p style={{ margin: 0, fontSize: '9px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          {bucket.label}
                        </p>
                        <p style={{ margin: '3px 0 0', fontSize: '15px', color: 'var(--text-primary)', fontFamily: 'var(--font-display)', fontWeight: 700 }}>
                          {bucket.count}
                        </p>
                      </div>
                    ))}
                  </div>

                  <p style={{
                    margin: '0 0 8px',
                    fontSize: '11px',
                    color: 'var(--text-muted)',
                    fontFamily: 'var(--font-ui)',
                    lineHeight: 1.5,
                  }}>
                    Indian Kanoon remains the spine. Verified government material and matter context stay visible alongside it.
                  </p>

                  {/* Manual search */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
                    borderRadius: '7px', padding: '6px 10px', marginBottom: '8px',
                  }}>
                    <Search size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                    <input
                      value={sourcesQuery}
                      onChange={e => setSourcesQuery(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && decomposeAndSearch(sourcesQuery, '', true)}
                      placeholder="Search a specific query..."
                      style={{
                        flex: 1, background: 'transparent', border: 'none', outline: 'none',
                        color: 'var(--text-primary)', fontSize: '12px',
                        fontFamily: 'var(--font-ui)',
                      }}
                    />
                    {searchingSources
                      ? <Loader2 size={12} className="animate-spin" style={{ color: 'var(--accent-primary)' }} />
                      : (
                        <button
                          onClick={() => decomposeAndSearch(sourcesQuery, '', true)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
                        >
                          <ChevronRight size={12} style={{ color: 'var(--text-muted)' }} />
                        </button>
                      )
                    }
                  </div>

                  {/* Decomposed issues pills */}
                  {decomposedIssues.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginBottom: '6px' }}>
                      {decomposedIssues.map((issue, i) => (
                        <span
                          key={i}
                          style={{
                            fontSize: '9px', padding: '2px 6px', borderRadius: '4px',
                            background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                            color: 'var(--text-muted)', fontFamily: 'var(--font-ui)',
                            maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            display: 'flex', alignItems: 'center', gap: '3px',
                          }}
                          title={issue}
                        >
                          <Tag size={8} style={{ flexShrink: 0 }} />
                          {issue}
                        </span>
                      ))}
                    </div>
                  )}

                  {Object.keys(sourceSummary).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
                      {Object.entries(sourceSummary).map(([label, count]) => (
                        <span
                          key={label}
                          style={{
                            fontSize: '9px',
                            padding: '2px 6px',
                            borderRadius: '999px',
                            background: 'var(--bg-base)',
                            border: '1px solid var(--border-subtle)',
                            color: 'var(--text-muted)',
                            fontFamily: 'var(--font-ui)',
                          }}
                        >
                          {label} · {count}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Court filters */}
                  <div className="flex gap-1 overflow-x-auto pb-1 whitespace-nowrap md:flex-wrap">
                    {[
                      { k: 'all', l: 'All' },
                      { k: 'sc', l: 'SC' },
                      { k: 'hc', l: 'HC' },
                      { k: 'tribunal', l: 'Tribunal' },
                      { k: 'gov', l: 'Gov' },
                    ].map(({ k, l }) => (
                      <button
                        key={k}
                        onClick={() => setSourceFilter(k)}
                        style={{
                          padding: '3px 8px', borderRadius: '5px', cursor: 'pointer',
                          fontSize: '10px', fontFamily: 'var(--font-ui)',
                          border: sourceFilter === k ? '1px solid var(--accent-primary)' : '1px solid transparent',
                          background: sourceFilter === k ? 'var(--accent-glow)' : 'transparent',
                          color: sourceFilter === k ? 'var(--accent-primary)' : 'var(--text-muted)',
                        }}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
                  {searchingSources && (
                    <SourceListSkeleton
                      title={sourcesQuery || researchTopic || 'Research query'}
                      description="Decomposing issues, checking Indian Kanoon, and assembling a usable authority stack."
                    />
                  )}
                  {!searchingSources && sources.length === 0 && <EmptySourcesState />}

                  {/* Pinned */}
                  {pinnedSources.length > 0 && <SectionLabel label="Pinned" />}
                  {pinnedSources.map(src => (
                    <SourceCard
                      key={src.id}
                      source={src}
                      isPinned
                      isActive={activeIds.has(src.id)}
                      isExpanded={expandedSource === src.id}
                      onToggleActive={() => toggleActive(src)}
                      onTogglePin={() => togglePin(src)}
                      onExpand={() => setExpandedSource(expandedSource === src.id ? null : src.id)}
                      onOpenPreview={openPreview}
                      onRemove={() => removeSource(src.id)}
                    />
                  ))}

                  {/* Grouped unpinned */}
                  {sourceGroups.map(({ label, srcs }) => (
                    <React.Fragment key={label}>
                      <IssueGroupLabel label={label} count={srcs.length} />
                      {srcs.map(src => (
                        <SourceCard
                          key={src.id}
                          source={src}
                          isPinned={false}
                          isActive={activeIds.has(src.id)}
                          isExpanded={expandedSource === src.id}
                          onToggleActive={() => toggleActive(src)}
                          onTogglePin={() => togglePin(src)}
                          onExpand={() => setExpandedSource(expandedSource === src.id ? null : src.id)}
                          onOpenPreview={openPreview}
                          onRemove={() => removeSource(src.id)}
                        />
                      ))}
                    </React.Fragment>
                  ))}
                </div>

                {sources.length > 0 && (
                  <div style={{
                    padding: '8px', borderTop: '1px solid var(--border-subtle)', flexShrink: 0,
                    display: 'flex', gap: '6px',
                  }}>
                    <button
                      onClick={() => setActiveSources(sources)}
                      style={{
                        flex: 1, padding: '6px 0', borderRadius: '6px',
                        background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
                        color: 'var(--text-secondary)', fontSize: '11px', cursor: 'pointer',
                        fontFamily: 'var(--font-ui)',
                      }}
                    >
                      Select all ({sources.length})
                    </button>
                    <button
                      onClick={() => setActiveSources([])}
                      style={{
                        padding: '6px 10px', borderRadius: '6px',
                        background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
                        color: 'var(--text-muted)', fontSize: '11px', cursor: 'pointer',
                        fontFamily: 'var(--font-ui)',
                      }}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </>
            )}

            {/* ── Knowledge Base tab ── */}
            {leftTab === 'knowledge' && (
              <>
                {/* Upload area */}
                <div style={{ padding: '10px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{
                      fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '0.1em', color: 'var(--text-muted)',
                      fontFamily: 'var(--font-ui)',
                    }}>
                      Your Documents
                    </span>
                    {activeUserDocIds.length > 0 && (
                      <span style={{
                        fontSize: '10px', padding: '2px 7px', borderRadius: '10px',
                        background: 'var(--accent-glow)', color: 'var(--accent-primary)',
                        border: '1px solid rgba(46,204,90,0.25)',
                        fontFamily: 'var(--font-ui)',
                      }}>
                        {activeUserDocIds.length} in context
                      </span>
                    )}
                  </div>

                  {/* Drag & drop upload zone */}
                  <div
                    onDragOver={e => { e.preventDefault(); setDocsDragOver(true); }}
                    onDragLeave={() => setDocsDragOver(false)}
                    onDrop={e => {
                      e.preventDefault();
                      setDocsDragOver(false);
                      uploadFiles(e.dataTransfer.files);
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      border: `2px dashed ${docsDragOver ? 'var(--accent-primary)' : 'var(--border-default)'}`,
                      borderRadius: '8px',
                      padding: '14px 10px',
                      textAlign: 'center',
                      cursor: uploadingDocs ? 'wait' : 'pointer',
                      background: docsDragOver ? 'var(--accent-glow)' : 'var(--bg-elevated)',
                      transition: 'all 0.15s',
                    }}
                  >
                    {uploadingDocs ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        <Loader2 size={14} className="animate-spin" style={{ color: 'var(--accent-primary)' }} />
                        <span style={{ fontSize: '11px', color: 'var(--accent-primary)', fontFamily: 'var(--font-ui)' }}>
                          Uploading & analysing...
                        </span>
                      </div>
                    ) : (
                      <>
                        <Upload size={16} style={{ color: 'var(--text-muted)', margin: '0 auto 4px' }} />
                        <p style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)', margin: 0 }}>
                          Drop files or click to upload
                        </p>
                        <p style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)', margin: '3px 0 0' }}>
                          PDF, DOCX, images, TXT · up to 50 MB each · batch upload supported
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.docx,.doc,.txt,.jpg,.jpeg,.png,.tiff,.webp"
                    style={{ display: 'none' }}
                    onChange={e => { uploadFiles(e.target.files); e.target.value = ''; }}
                  />
                </div>

                {/* Document list */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
                  {loadingDocs && (
                    <DocumentShelfSkeleton />
                  )}
                  {!loadingDocs && userDocs.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <BookOpen className="w-12 h-12 text-gray-300 mb-4" />
                      <h3 className="text-lg font-medium text-gray-600 mb-2">Start your research desk</h3>
                      <p className="text-sm text-gray-400 max-w-xs">
                        Upload briefs, contracts, statutes, or notes so Advoq can ground the desk in your actual record before drafting.
                      </p>
                    </div>
                  )}
                  {userDocs.map(doc => (
                    <UserDocCard
                      key={doc.upload_id}
                      doc={doc}
                      isActive={activeUserDocIds.includes(doc.upload_id)}
                      onToggle={() => toggleUserDoc(doc.upload_id)}
                      onOpenPreview={() => openPreview(getUploadPreviewUrl(doc.upload_id), doc.filename)}
                      onDelete={() => deleteUserDoc(doc.upload_id)}
                    />
                  ))}
                </div>

                {/* Footer actions */}
                {userDocs.length > 0 && (
                  <div style={{
                    padding: '8px', borderTop: '1px solid var(--border-subtle)', flexShrink: 0,
                    display: 'flex', gap: '6px',
                  }}>
                    <button
                      onClick={() => setActiveUserDocIds(userDocs.map(d => d.upload_id))}
                      style={{
                        flex: 1, padding: '6px 0', borderRadius: '6px',
                        background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
                        color: 'var(--text-secondary)', fontSize: '11px', cursor: 'pointer',
                        fontFamily: 'var(--font-ui)',
                      }}
                    >
                      Use all ({userDocs.length})
                    </button>
                    <button
                      onClick={() => setActiveUserDocIds([])}
                      style={{
                        padding: '6px 10px', borderRadius: '6px',
                        background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
                        color: 'var(--text-muted)', fontSize: '11px', cursor: 'pointer',
                        fontFamily: 'var(--font-ui)',
                      }}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Drag handle: left ↔ middle */}
          <div
            onMouseDown={e => startLeftDrag(e, 1)}
            className="hidden md:block"
            style={{
              width: '5px', flexShrink: 0, cursor: 'col-resize',
              background: 'var(--border-subtle)', transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-primary)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--border-subtle)'}
          />

          {/* ══ MIDDLE: Document Editor ════════════════════════════ */}
          <div className="research-agent-document-stage" style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            background: 'var(--workspace-canvas-bg)',
            overflow: 'hidden', minWidth: 0,
          }}>
            {/* Doc toolbar */}
            <div className="research-agent-toolbar overflow-x-auto" style={{
              padding: '8px 20px',
              borderBottom: '1px solid var(--workspace-surface-border)',
              background: 'var(--workspace-surface-bg)',
              display: 'flex', alignItems: 'center', gap: '8px',
              flexShrink: 0,
            }}>
              {/* TOC toggle */}
              <ToolbarBtn
                active={showTOC}
                onClick={() => setShowTOC(v => !v)}
                icon={<List size={13} />}
                label="Contents"
              />

              <div style={{ flex: 1 }} />

              {/* Copy */}
              <ToolbarBtn onClick={copyDocument} icon={<Copy size={13} />} label="Copy" />

              {/* Export */}
              <ToolbarBtn onClick={exportMarkdown} icon={<Download size={13} />} label="Export" />

              {/* Save to Knowledge Base */}
              <ToolbarBtn
                onClick={saveDocToKnowledgeBase}
                icon={<Database size={13} />}
                label="Save to KB"
                title="Index this research document in the Knowledge Base"
              />

              {/* Save */}
              <button
                onClick={saveDocument}
                disabled={savingDoc}
                style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  padding: '5px 13px', borderRadius: '6px', border: 'none',
                  background: 'var(--accent-primary)', color: '#f3eadb',
                  fontSize: '12px', cursor: savingDoc ? 'wait' : 'pointer',
                  fontWeight: 600, fontFamily: 'var(--font-ui)',
                }}
              >
                {savingDoc ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                Save
              </button>
            </div>

            {/* Editor body */}
            <div className="research-agent-document-stage flex flex-col md:flex-row" style={{ flex: 1, overflow: 'hidden' }}>
              {/* TOC sidebar */}
              {showTOC && (
                <div style={{
                  width: isMobile ? '100%' : '170px', flexShrink: 0,
                  borderRight: isMobile ? 'none' : '1px solid var(--border-subtle)',
                  borderBottom: isMobile ? '1px solid var(--border-subtle)' : 'none',
                  background: 'var(--bg-surface)',
                  padding: '16px 10px', overflowY: 'auto',
                }}>
                  <p style={{
                    fontSize: '9px', fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.12em', color: 'var(--text-muted)',
                    marginBottom: '10px', fontFamily: 'var(--font-ui)',
                  }}>
                    Contents
                  </p>
                  {docSections.map((sec, i) => (
                    <button
                      key={sec.id}
                      onClick={() => sectionRefs.current[sec.id]?.scrollIntoView({ behavior: 'smooth' })}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '5px 8px', borderRadius: '5px', border: 'none',
                        background: 'transparent', color: 'var(--text-secondary)',
                        fontSize: '11px', cursor: 'pointer',
                        fontFamily: 'var(--font-ui)', marginBottom: '2px',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <span style={{ color: 'var(--text-muted)', marginRight: '5px' }}>{i + 1}.</span>
                      {sec.title}
                    </button>
                  ))}
                </div>
              )}

              {/* Document scroll area */}
                <div
                  ref={docScrollRef}
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: workspaceStarted
                      ? (isMobile ? '20px 16px 24px' : '32px 40px')
                      : (isMobile ? '16px' : '20px 24px 24px'),
                  }}
                >
                {!workspaceStarted ? (
                  <ResearchKickoffPanel
                    researchTopic={researchTopic}
                    setResearchTopic={setResearchTopic}
                    researchType={researchType}
                    setResearchType={setResearchType}
                    workflowMode={workflowMode}
                    setWorkflowMode={setWorkflowMode}
                    primaryCourt={primaryCourt}
                    setPrimaryCourt={setPrimaryCourt}
                    linkedDoc={linkedDoc}
                    matterContext={matterContext}
                    onDictate={appendResearchTopic}
                    onUseStarter={starter => setResearchTopic(starter)}
                    onStart={() => startResearch()}
                  />
                ) : (
                  <div className="research-agent-paper">
                    <input
                      value={docTitle}
                      onChange={e => setDocTitle(e.target.value)}
                      placeholder="Document Title"
                      style={{
                        width: '100%', background: 'transparent', border: 'none', outline: 'none',
                        fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)',
                        fontFamily: 'var(--font-display)', marginBottom: '8px',
                        borderBottom: '2px solid transparent', paddingBottom: '4px',
                        transition: 'border-color 0.15s',
                      }}
                      onFocus={e => (e.target.style.borderBottomColor = 'var(--accent-primary)')}
                      onBlur={e => (e.target.style.borderBottomColor = 'transparent')}
                    />

                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
                      <ContextBadge label={currentFlow.label} />
                      <ContextBadge label={currentResearchType.label} />
                      <ContextBadge label={currentCourt.label} />
                      {linkedDoc?.title && <ContextBadge label={`Linked doc: ${linkedDoc.title}`} />}
                      {matterContext?.name && <ContextBadge label={`Matter: ${matterContext.name}`} />}
                    </div>

                    <ResearchDeskOverview
                      researchTopic={researchTopic}
                      workflowDetails={workflowDetails}
                      authorityMap={deskModel.authorityMap}
                      issueMap={deskModel.issueMap}
                      missingAuthorities={deskModel.missingAuthorities}
                      proofGaps={deskModel.proofGaps}
                      weaknessSignals={deskModel.weaknessSignals}
                      readiness={deskModel.readiness}
                      jurisdictionMap={deskModel.jurisdictionMap}
                      sourceSummary={sourceSummary}
                      trustSummary={trustSummary}
                      matterContext={matterContext}
                      linkedDoc={linkedDoc}
                    />

                    <div
                      data-testid="research-desk-grid"
                      className="grid grid-cols-1 gap-4 md:grid-cols-2"
                      style={{ marginBottom: '22px' }}
                    >
                      <IssueMapCard issues={deskModel.issueMap} />
                      <AuthorityMapCard authorityMap={deskModel.authorityMap} />
                      <ResearchSignalsCard
                        title="What Authority Is Still Missing"
                        subtitle="The desk flags what is not yet safe to assume."
                        icon={Target}
                        items={deskModel.missingAuthorities}
                        emptyState="The current stack has the minimum authority layers expected for this mode."
                        tone="warning"
                        testId="missing-authority-card"
                      />
                      <ResearchSignalsCard
                        title="Contradictions & Weaknesses"
                        subtitle="Pressure points before the other side or the bench reaches them."
                        icon={RotateCcw}
                        items={deskModel.weaknessSignals}
                        emptyState="No contradiction signals surfaced yet."
                        tone="neutral"
                        testId="weakness-card"
                      />
                      <ResearchSignalsCard
                        title="Proof Gaps & Record Tasks"
                        subtitle="What still needs documents, chronology, or admissions before hard use."
                        icon={Paperclip}
                        items={deskModel.proofGaps}
                        emptyState="No obvious record gaps surfaced yet."
                        tone="neutral"
                        testId="proof-gap-card"
                      />
                      <ResearchSignalsCard
                        title="Relief Posture"
                        subtitle="Primary ask, fallback ask, and safest way to present the issue."
                        icon={Scale}
                        items={deskModel.reliefPosture}
                        emptyState="No relief posture has been framed yet."
                        tone="neutral"
                        testId="relief-posture-card"
                      />
                    </div>

                    <WorkflowWorkbenchCard
                      workflowMode={workflowMode}
                      currentFlow={currentFlow}
                      onSwitchMode={switchWorkflowMode}
                      onApplyTemplate={applyTemplate}
                    />

                    {activeSources.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '28px' }}>
                        {activeSources.slice(0, 5).map(s => (
                          <button
                            key={s.id}
                            onClick={() => openPreview(s.url, s.title)}
                            style={{
                              fontSize: '10px', padding: '2px 8px', borderRadius: '10px',
                              background: 'var(--accent-glow)', color: 'var(--accent-primary)',
                              border: '1px solid rgba(46,204,90,0.2)',
                              textDecoration: 'none', fontFamily: 'var(--font-ui)',
                              display: 'flex', alignItems: 'center', gap: '3px',
                              cursor: 'pointer',
                            }}
                          >
                            <CheckCircle size={9} /> {s.title?.split(' ').slice(0, 4).join(' ')}…
                          </button>
                        ))}
                        {activeSources.length > 5 && (
                          <span style={{
                            fontSize: '10px', padding: '2px 8px', borderRadius: '10px',
                            background: 'var(--bg-elevated)', color: 'var(--text-muted)',
                            fontFamily: 'var(--font-ui)',
                          }}>
                            +{activeSources.length - 5} more
                          </span>
                        )}
                      </div>
                    )}

                    {contextDebug.length > 0 && (
                      <ResearchContextCard
                        items={contextDebug}
                        onOpenPreview={openPreview}
                      />
                    )}

                    {(kickoffResearching || kickoffReport) && (
                      <KickoffBriefCard
                        loading={kickoffResearching}
                        status={kickoffStatus}
                        report={kickoffReport}
                        cases={kickoffCases}
                        docs={kickoffUserDocs}
                        onOpenPreview={openPreview}
                        onInsert={insertKickoffIntoDocument}
                      />
                    )}

                    {docSections.map((sec, idx) => (
                      <DocumentSection
                        key={sec.id}
                        section={sec}
                        index={idx}
                        total={docSections.length}
                        isGenerating={generatingSection === sec.id}
                        sectionRef={el => (sectionRefs.current[sec.id] = el)}
                        onChange={(field, val) => updateSection(sec.id, field, val)}
                        onRemove={() => removeSection(sec.id)}
                        onMoveUp={() => moveSection(sec.id, 'up')}
                        onMoveDown={() => moveSection(sec.id, 'down')}
                        onGenerate={() => generateSection(sec.id)}
                      />
                    ))}

                    <button
                      onClick={addSection}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                        width: '100%', padding: '11px 16px', borderRadius: '8px',
                        border: '1px dashed var(--border-default)', background: 'transparent',
                        color: 'var(--text-muted)', fontSize: '13px', cursor: 'pointer',
                        fontFamily: 'var(--font-ui)', marginTop: '8px',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = 'var(--accent-primary)';
                        e.currentTarget.style.color = 'var(--accent-primary)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = 'var(--border-default)';
                        e.currentTarget.style.color = 'var(--text-muted)';
                      }}
                    >
                      <Plus size={14} /> Add Section
                    </button>
                  </div>
                )}

                <div style={{ height: '80px' }} />
              </div>
            </div>
          </div>

          {showDeskCounsel ? (
            <>
              {isMobile && (
                <button
                  type="button"
                  aria-label="Close desk counsel"
                  className="fixed inset-0 z-40 bg-black/40 md:hidden"
                  onClick={() => setShowDeskCounsel(false)}
                />
              )}
              {/* Drag handle: middle ↔ right */}
              <div
                onMouseDown={e => startRightDrag(e, -1)}
                className="hidden md:block"
                style={{
                  width: '5px', flexShrink: 0, cursor: 'col-resize',
                  background: 'var(--border-subtle)', transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-primary)'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--border-subtle)'}
              />

              {/* ══ RIGHT: Agent Chat ══════════════════════════════════ */}
              <div className="research-agent-panel research-agent-panel--right fixed inset-y-0 right-0 z-50 w-full max-w-[min(24rem,100vw)] md:relative md:z-auto md:w-auto md:max-w-none" style={{
                width: isMobile ? undefined : rightPanelWidth, flexShrink: 0,
                background: 'var(--workspace-rail-bg)',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
                borderLeft: '1px solid var(--workspace-surface-border)',
                boxShadow: 'var(--workspace-surface-shadow-soft)',
                position: isMobile ? 'fixed' : 'relative',
              }}>
            {/* Agent header */}
            <div className="research-agent-chat-header" style={{
              padding: '12px 16px 10px',
              borderBottom: '1px solid var(--border-subtle)',
              display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0,
            }}>
              <div style={{
                width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                background: 'var(--bg-elevated)',
                border: '1px solid color-mix(in srgb, var(--accent-primary) 24%, var(--border-default))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 10px 24px rgba(39, 28, 18, 0.08)',
              }}>
                <Bot size={15} style={{ color: 'var(--accent-primary)' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                  fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)',
                  fontFamily: 'var(--font-ui)', lineHeight: 1.2,
                }}>
                  Research Desk Counsel
                </p>
                <p style={{
                  fontSize: '10px', fontFamily: 'var(--font-ui)',
                  color: agentStreaming ? 'var(--accent-primary)' : 'var(--text-muted)',
                }}>
                  {agentStreaming
                    ? 'Reviewing authorities and drafting with you...'
                    : (activeSources.length + activeUserDocIds.length) > 0
                      ? `${activeSources.length} source${activeSources.length !== 1 ? 's' : ''} · ${activeUserDocIds.length} doc${activeUserDocIds.length !== 1 ? 's' : ''} in context`
                      : workflowDetails.summary}
                </p>
              </div>
              {agentStreaming && (
                <button
                  onClick={() => agentAbortRef.current?.abort()}
                  style={{
                    padding: '4px 8px', borderRadius: '5px', border: '1px solid rgba(231,76,60,0.3)',
                    background: 'rgba(231,76,60,0.1)', color: '#e74c3c',
                    fontSize: '10px', cursor: 'pointer', fontFamily: 'var(--font-ui)',
                  }}
                >
                  Stop
                </button>
              )}
            </div>

            {/* Quick action chips */}
            <div style={{
              padding: '10px', borderBottom: '1px solid var(--border-subtle)',
              flexShrink: 0, overflowY: 'auto', maxHeight: '220px',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {Object.entries(quickActionGroups).map(([group, actions]) => (
                  <div key={group} style={{
                    borderRadius: '12px',
                    border: '1px solid var(--border-subtle)',
                    background: 'var(--bg-elevated)',
                    padding: '8px',
                  }}>
                    <p style={{
                      margin: '0 0 6px',
                      fontSize: '10px',
                      color: 'var(--text-muted)',
                      fontFamily: 'var(--font-ui)',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}>
                      {group}
                    </p>
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                      {actions.map(action => (
                        <button
                          key={action.id}
                          onClick={() => { setAgentInput(action.prompt); agentInputRef.current?.focus(); }}
                          style={{
                            padding: '5px 9px', borderRadius: '12px', cursor: 'pointer',
                            border: '1px solid var(--border-default)',
                            background: 'var(--bg-base)', color: 'var(--text-secondary)',
                            fontSize: '11px', fontFamily: 'var(--font-ui)',
                            display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
                            transition: 'all 0.12s',
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.borderColor = 'var(--accent-primary)';
                            e.currentTarget.style.color = 'var(--accent-primary)';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.borderColor = 'var(--border-default)';
                            e.currentTarget.style.color = 'var(--text-secondary)';
                          }}
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                <div style={{
                  borderRadius: '12px',
                  background: 'color-mix(in srgb, var(--accent-glow) 56%, var(--bg-elevated))',
                  border: '1px solid color-mix(in srgb, var(--accent-primary) 18%, var(--border-default))',
                  padding: '8px 10px',
                }}>
                  <p style={{ margin: 0, fontSize: '10px', color: 'var(--accent-primary)', fontFamily: 'var(--font-ui)', fontWeight: 700 }}>
                    Active mode
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', lineHeight: 1.5 }}>
                    {workflowDetails.title} · {workflowDetails.outputLabel}
                  </p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {agentMessages.map((msg, i) => (
                <AgentMessage key={i} message={msg} openPreview={openPreview} />
              ))}
              <div ref={agentChatEndRef} />
            </div>

            {/* Input */}
            <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border-subtle)', flexShrink: 0 }}>
              {agentStreaming ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 12px',
                  color: 'var(--accent-primary)', fontSize: '12px',
                  fontFamily: 'var(--font-ui)',
                }}>
                  <Loader2 size={13} className="animate-spin" />
                  <span>Thinking...</span>
                </div>
              ) : (
                <>
                  {/* Pending file pill */}
                  {chatPendingFile && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '5px 10px', marginBottom: '6px',
                      background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
                      borderRadius: '6px',
                    }}>
                      {chatUploadingFile
                        ? <Loader2 size={12} className="animate-spin" style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                        : <FileText size={12} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                      }
                      <span style={{
                        flex: 1, fontSize: '11px', color: 'var(--text-secondary)',
                        fontFamily: 'var(--font-ui)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {chatUploadingFile ? `Uploading "${chatPendingFile.name}"...` : `"${chatPendingFile.name}" — will be added to context`}
                      </span>
                      {!chatUploadingFile && (
                        <button onClick={() => setChatPendingFile(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '1px', color: 'var(--text-muted)', display: 'flex' }}>
                          <X size={11} />
                        </button>
                      )}
                    </div>
                  )}
                  <div style={{
                    display: 'flex', gap: '6px', alignItems: 'flex-end',
                    background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
                    borderRadius: '10px', padding: '8px 12px',
                  }}>
                    {/* Paperclip */}
                    <input
                      ref={chatFileInputRef}
                      type="file"
                      accept=".pdf,.docx,.doc,.txt"
                      style={{ display: 'none' }}
                      onChange={handleChatFileSelect}
                    />
                    <button
                      onClick={() => chatFileInputRef.current?.click()}
                      disabled={chatUploadingFile}
                      title="Attach a document to this conversation"
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                        color: chatPendingFile ? 'var(--accent-primary)' : 'var(--text-muted)',
                        display: 'flex', alignItems: 'center', flexShrink: 0,
                      }}
                    >
                      <Paperclip size={14} />
                    </button>
                    <JollyDictationButton
                      compact
                      disabled={agentStreaming || chatUploadingFile}
                      label="Jolly"
                      onAppendText={appendAgentInput}
                    />
                    <textarea
                      ref={agentInputRef}
                      value={agentInput}
                      onChange={e => setAgentInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendAgentMessage();
                        }
                      }}
                      placeholder="Ask to write a section, find cases, attach a file..."
                      rows={2}
                      style={{
                        flex: 1, background: 'transparent', border: 'none', outline: 'none',
                        resize: 'none', color: 'var(--text-primary)', fontSize: '13px',
                        fontFamily: 'var(--font-ui)', lineHeight: '1.5',
                      }}
                    />
                    <button
                      onClick={sendAgentMessage}
                      disabled={!agentInput.trim()}
                      style={{
                        width: '32px', height: '32px', borderRadius: '8px', border: 'none',
                        background: agentInput.trim() ? 'var(--accent-primary)' : 'var(--bg-surface)',
                        color: agentInput.trim() ? 'var(--accent-foreground)' : 'var(--text-muted)',
                        cursor: agentInput.trim() ? 'pointer' : 'not-allowed',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        transition: 'all 0.12s',
                      }}
                    >
                      <Send size={14} />
                    </button>
                  </div>
                  <p style={{
                    fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px',
                    fontFamily: 'var(--font-ui)',
                  }}>
                    Enter to send · Shift+Enter for newline · Jolly dictation flows into the composer · 📎 attach PDF/DOCX/TXT
                  </p>
                </>
              )}
            </div>
              </div>
            </>
          ) : null}

        </div>
      </main>

      {previewUrl && (
        <LinkPreviewPanel
          url={previewUrl}
          title={previewTitle}
          onClose={() => { setPreviewUrl(null); setPreviewTitle(''); }}
        />
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function HeaderSelect({ label, value, onChange, options }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span style={{
        fontSize: '10px',
        color: 'var(--text-muted)',
        fontFamily: 'var(--font-ui)',
      }}>
        {label}
      </span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-default)',
          color: 'var(--text-secondary)',
          fontSize: '11px',
          padding: '5px 10px',
          borderRadius: '999px',
          cursor: 'pointer',
          fontFamily: 'var(--font-ui)',
          outline: 'none',
        }}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}

function HeaderLinkButton({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="shrink-0 whitespace-nowrap"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 10px',
        borderRadius: '999px',
        border: '1px solid var(--border-default)',
        background: 'var(--bg-elevated)',
        color: 'var(--text-secondary)',
        cursor: 'pointer',
        fontSize: '11px',
        fontFamily: 'var(--font-ui)',
      }}
    >
      {icon}
      {label}
    </button>
  );
}

function ContextBadge({ label }) {
  return (
    <span className="max-w-full truncate" title={label} style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 10px',
      borderRadius: '999px',
      background: 'var(--bg-elevated)',
      border: '1px solid color-mix(in srgb, var(--border-default) 82%, transparent)',
      color: 'var(--text-secondary)',
      fontSize: '11px',
      fontFamily: 'var(--font-ui)',
      maxWidth: '100%',
    }}>
      {label}
    </span>
  );
}

function detectResearchMeta(text) {
  const lower = (text || '').toLowerCase();
  // Research type detection
  let detectedType = null;
  const casePattern = /\b[A-Z][a-zA-Z\s&.'-]{2,40}\s+v(?:s)?\.?\s+[A-Z][a-zA-Z\s&.'-]{2,40}/;
  if (casePattern.test(text)) detectedType = 'CASE';
  else if (/\bsection\s+\d|\bact\b|\bstatute|\brule\s+\d|\bnotification|\bnotification no\.|\bschedule\s+\d/i.test(lower)) detectedType = 'STATUTE';
  else if (/\bjustice\s+[A-Z]|\badvocate\s+[A-Z]|\bjudge\s+[A-Z]|\bcj\s|\bj\.\s+[A-Z]/i.test(text)) detectedType = 'PERSON';

  // Workflow mode detection
  let detectedFlow = null;
  if (/\bbench question|\bjudge.*ask|\blikely judge question|\bwhat will the bench ask/i.test(lower)) detectedFlow = 'BENCH';
  else if (/\bhearing|\barguments|\boral sub|\bnext date/i.test(lower)) detectedFlow = 'HEARING';
  else if (/\bpetition|\bwrit|\bapplication|\bfiled|\bfiling|\bground of challenge/i.test(lower)) detectedFlow = 'PETITION';
  else if (/\bcase brief|\bjudgment.*brief|\bdecision.*brief|\bbrief of/i.test(lower)) detectedFlow = 'BRIEF';
  else if (/\birac|\bexam|\bproblem question|\banswer structure/i.test(lower)) detectedFlow = 'IRAC';

  return { detectedType, detectedFlow };
}

function ResearchKickoffPanel({
  researchTopic,
  setResearchTopic,
  researchType,
  setResearchType,
  workflowMode,
  setWorkflowMode,
  primaryCourt,
  setPrimaryCourt,
  linkedDoc,
  matterContext,
  onDictate,
  onUseStarter,
  onStart,
}) {
  const [autoDetectedHint, setAutoDetectedHint] = React.useState(null);

  const handleTopicChange = (e) => {
    const text = e.target.value;
    setResearchTopic(text);
    if (text.length < 20) { setAutoDetectedHint(null); return; }
    const { detectedType, detectedFlow } = detectResearchMeta(text);
    if (detectedType) { setResearchType(detectedType); }
    if (detectedFlow) { setWorkflowMode(detectedFlow); }
    if (detectedType || detectedFlow) {
      const typeName = detectedType ? RESEARCH_TYPES.find(t => t.value === detectedType)?.label : null;
      const flowName = detectedFlow ? WORKSPACE_FLOWS.find(f => f.value === detectedFlow)?.label : null;
      setAutoDetectedHint([typeName, flowName].filter(Boolean).join(' · '));
    } else {
      setAutoDetectedHint(null);
    }
  };

  return (
    <div className="research-kickoff-panel">
      {(linkedDoc?.title || matterContext?.name) && (
        <div className="research-kickoff-panel__context">
          {linkedDoc?.title && <ContextBadge label={`Linked document: ${linkedDoc.title}`} />}
          {matterContext?.name && <ContextBadge label={`Matter: ${matterContext.name}`} />}
        </div>
      )}

      <div className="research-kickoff-panel__grid">
        <div className="research-kickoff-panel__intake">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--text-muted)',
                fontFamily: 'var(--font-ui)',
              }}>
                Matter Intake
              </label>
              <p style={{
                margin: '6px 0 0',
                color: 'var(--text-secondary)',
                fontSize: '13px',
                lineHeight: 1.55,
                fontFamily: 'var(--font-ui)',
              }}>
                Open a new research desk or reopen previous work.
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <FeatureHistoryMenu
                feature="research"
                label="Previous Work"
                buttonClassName="app-chat-button"
              />
              <JollyDictationButton compact label="Jolly intake" onAppendText={onDictate} />
            </div>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap',
            marginBottom: '8px',
          }}>
            <label style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-ui)',
          }}>
            Research Question
          </label>
          </div>
          {autoDetectedHint && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', padding: '6px 12px', borderRadius: '8px', background: 'color-mix(in srgb, var(--accent-primary) 10%, var(--bg-elevated))', border: '1px solid color-mix(in srgb, var(--accent-primary) 20%, var(--border-subtle))' }}>
              <Sparkles size={11} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
              <span style={{ fontSize: '11px', color: 'var(--accent-primary)', fontFamily: 'var(--font-ui)', fontWeight: 600 }}>
                Auto-detected: {autoDetectedHint}
              </span>
            </div>
          )}
          <textarea
            value={researchTopic}
            onChange={handleTopicChange}
            placeholder="Example: Maintainability of Section 9 arbitration petition after termination of contract and invocation disputes"
            rows={3}
            className="research-kickoff-panel__textarea"
          />

          <div className="research-kickoff-panel__starters">
            <p style={kickoffLabelStyle}>Common starting points</p>
            <div className="research-kickoff-panel__starter-grid">
              {MATTER_STARTERS.map(starter => (
                <button
                  key={starter}
                  onClick={() => onUseStarter(starter)}
                  className="research-kickoff-panel__starter"
                >
                  {starter}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="research-kickoff-panel__controls">
          <div className="research-kickoff-panel__selectors flex flex-nowrap gap-3 overflow-x-auto pb-1 md:grid md:grid-cols-3 md:gap-4 md:overflow-visible md:pb-0">
            <div className="research-kickoff-panel__field min-w-[240px] md:min-w-0">
              <p style={kickoffLabelStyle}>Research lens</p>
              <select
                className="advoq-input app-chat-select research-kickoff-panel__select"
                value={researchType}
                onChange={(e) => setResearchType(e.target.value)}
              >
                {RESEARCH_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              <p className="research-kickoff-panel__helper">
                {(RESEARCH_TYPES.find(type => type.value === researchType) || RESEARCH_TYPES[0]).prompt}
              </p>
            </div>

            <div className="research-kickoff-panel__field min-w-[240px] md:min-w-0">
              <p style={kickoffLabelStyle}>Deliverable</p>
              <select
                className="advoq-input app-chat-select research-kickoff-panel__select"
                value={workflowMode}
                onChange={(e) => setWorkflowMode(e.target.value)}
              >
                {WORKSPACE_FLOWS.map((flow) => (
                  <option key={flow.value} value={flow.value}>{flow.label}</option>
                ))}
              </select>
              <p className="research-kickoff-panel__helper">
                {(WORKSPACE_FLOWS.find(flow => flow.value === workflowMode) || WORKSPACE_FLOWS[0]).helper}
              </p>
            </div>

            <div className="research-kickoff-panel__field min-w-[240px] md:min-w-0">
              <p style={kickoffLabelStyle}>Court lens</p>
              <select
                className="advoq-input app-chat-select research-kickoff-panel__select"
                value={primaryCourt}
                onChange={(e) => setPrimaryCourt(e.target.value)}
              >
                {COURT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="research-kickoff-panel__footer">
            <button
              data-testid="research-start-button"
              onClick={onStart}
              disabled={!researchTopic.trim()}
              style={{
                padding: '11px 16px',
                borderRadius: '12px',
                border: 'none',
                background: researchTopic.trim() ? 'var(--accent-primary)' : 'var(--bg-base)',
                color: researchTopic.trim() ? 'var(--accent-foreground)' : 'var(--text-muted)',
                fontSize: '13px',
                fontWeight: 700,
                cursor: researchTopic.trim() ? 'pointer' : 'not-allowed',
                fontFamily: 'var(--font-ui)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                justifyContent: 'center',
              }}
            >
              <Sparkles size={14} />
              Open research workspace
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResearchDeskOverview({
  researchTopic,
  workflowDetails,
  authorityMap,
  issueMap,
  missingAuthorities,
  proofGaps,
  weaknessSignals,
  readiness,
  jurisdictionMap,
  sourceSummary,
  trustSummary,
  matterContext,
  linkedDoc,
}) {
  return (
    <div style={{
      borderRadius: '24px',
      border: '1px solid var(--workspace-surface-border)',
      background: 'var(--workspace-hero-bg)',
      padding: '20px 22px',
      marginBottom: '18px',
      boxShadow: 'var(--workspace-surface-shadow-soft)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '18px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: '11px', color: 'var(--accent-primary)', fontFamily: 'var(--font-ui)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Research Desk
          </p>
          <h3 style={{ margin: '6px 0 0', fontSize: '24px', color: 'var(--text-primary)', fontFamily: 'var(--font-display)', lineHeight: 1.2 }}>
            {researchTopic || 'Matter opened'}
          </h3>
          <p style={{ margin: '8px 0 0', fontSize: '13px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)', lineHeight: 1.6, maxWidth: '760px' }}>
            {workflowDetails.summary}
          </p>
          {(matterContext?.name || linkedDoc?.title) && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
              {matterContext?.name && <ContextBadge label={`Matter continuity: ${matterContext.name}`} />}
              {linkedDoc?.title && <ContextBadge label={`Linked document: ${linkedDoc.title}`} />}
            </div>
          )}
          {trustSummary?.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
              {trustSummary.slice(0, 4).map((item) => (
                <SourceTrustBadge
                  key={item.label}
                  label={item.label}
                  compact
                  count={item.count}
                />
              ))}
            </div>
          )}
          {readiness?.postureLabel && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
              <ContextBadge label={`Readiness: ${readiness.postureLabel}`} />
              {Number.isFinite(readiness.overallScore) && <ContextBadge label={`Score: ${readiness.overallScore}`} />}
              {jurisdictionMap?.length > 0 && jurisdictionMap.slice(0, 3).map((item) => (
                <ContextBadge key={item.forum} label={`${item.label}: ${item.count}`} />
              ))}
            </div>
          )}
        </div>

        <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 md:w-auto" style={{ minWidth: 0 }}>
          <DeskStatCard label="Issues mapped" value={issueMap.length} helper={`${issueMap.filter(issue => issue.status === 'mapped').length} already covered`} />
          <DeskStatCard label="Top authorities" value={authorityMap.topAuthorities.length} helper={`${authorityMap.activeCount} active in context`} />
          <DeskStatCard label="Open gaps" value={missingAuthorities.length} helper={missingAuthorities.length ? 'Needs another pass' : 'Court-ready shape'} />
          <DeskStatCard
            label="Court readiness"
            value={readiness?.overallScore ?? weaknessSignals.length}
            helper={readiness?.postureLabel || (Object.keys(sourceSummary || {}).length ? `${Object.keys(sourceSummary || {}).length} source families scanned` : 'From current brief and authorities')}
          />
          <DeskStatCard
            label="Proof gaps"
            value={proofGaps.length}
            helper={proofGaps.length ? 'Record still needs another pass' : 'No obvious record exposure'}
          />
        </div>
      </div>
    </div>
  );
}

function DeskStatCard({ label, value, helper }) {
  return (
    <div style={{
      borderRadius: '14px',
      border: '1px solid var(--workspace-surface-border)',
      background: 'var(--workspace-chip-bg)',
      padding: '10px 12px',
    }}>
      <p style={{ margin: 0, fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {label}
      </p>
      <p style={{ margin: '4px 0 0', fontSize: '22px', color: 'var(--text-primary)', fontFamily: 'var(--font-display)', fontWeight: 700 }}>
        {value}
      </p>
      <p style={{ margin: '2px 0 0', fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
        {helper}
      </p>
    </div>
  );
}

function IssueMapCard({ issues }) {
  return (
    <div
      data-testid="issue-map-card"
      style={{
        borderRadius: '18px',
        border: '1px solid color-mix(in srgb, var(--border-default) 82%, transparent)',
        background: 'var(--app-panel-bg)',
        padding: '16px 18px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <BookOpen size={15} style={{ color: 'var(--accent-primary)' }} />
        <div>
          <p style={{ margin: 0, fontSize: '11px', color: 'var(--accent-primary)', fontFamily: 'var(--font-ui)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Issue Map
          </p>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
            What the matter actually turns on.
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {issues.length === 0 && (
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
            Start research to decompose the issue.
          </p>
        )}
        {issues.map(issue => (
          <div key={issue.id} style={{
            borderRadius: '12px',
            border: '1px solid var(--border-subtle)',
            background: 'var(--bg-elevated)',
            padding: '10px 12px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
              <strong style={{ fontSize: '12px', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', lineHeight: 1.5 }}>
                {issue.title}
              </strong>
              <span style={{
                fontSize: '10px',
                padding: '2px 8px',
                borderRadius: '999px',
                background: issue.status === 'mapped' ? 'var(--accent-glow)' : 'rgba(184, 92, 56, 0.12)',
                color: issue.status === 'mapped' ? 'var(--accent-primary)' : '#9a5937',
                fontFamily: 'var(--font-ui)',
                whiteSpace: 'nowrap',
              }}>
                {issue.coverageLabel}
              </span>
            </div>
            {(issue.authorityTier || issue.proofStatus) && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                {issue.authorityTier && (
                  <span style={{
                    fontSize: '10px',
                    color: 'var(--text-muted)',
                    fontFamily: 'var(--font-ui)',
                  }}>
                    Authority: {issue.authorityTier}
                  </span>
                )}
                {issue.proofStatus && (
                  <span style={{
                    fontSize: '10px',
                    color: 'var(--text-muted)',
                    fontFamily: 'var(--font-ui)',
                  }}>
                    Proof: {issue.proofStatus}
                  </span>
                )}
              </div>
            )}
            {issue.detail && (
              <p style={{ margin: '6px 0 0', fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)', lineHeight: 1.5 }}>
                {issue.detail}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AuthorityMapCard({ authorityMap }) {
  return (
    <div
      data-testid="authority-map-card"
      style={{
        borderRadius: '18px',
        border: '1px solid color-mix(in srgb, var(--border-default) 82%, transparent)',
        background: 'var(--app-panel-bg)',
        padding: '16px 18px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <Scale size={15} style={{ color: 'var(--accent-primary)' }} />
        <div>
          <p style={{ margin: 0, fontSize: '11px', color: 'var(--accent-primary)', fontFamily: 'var(--font-ui)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Authority Map
          </p>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
            Which authorities carry the argument and which only support it.
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', marginBottom: '12px' }}>
        {authorityMap.buckets.slice(0, 4).map(bucket => (
          <div key={bucket.key} style={{
            borderRadius: '12px',
            border: '1px solid var(--border-subtle)',
            background: 'var(--bg-elevated)',
            padding: '10px 12px',
          }}>
            <p style={{ margin: 0, fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {bucket.label}
            </p>
            <p style={{ margin: '4px 0 0', fontSize: '19px', color: 'var(--text-primary)', fontFamily: 'var(--font-display)', fontWeight: 700 }}>
              {bucket.count}
            </p>
            <p style={{ margin: '3px 0 0', fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)', lineHeight: 1.4 }}>
              {bucket.helper}
            </p>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
        {authorityMap.topAuthorities.slice(0, 3).map(authority => (
          <div key={authority.id} style={{
            borderRadius: '12px',
            border: '1px solid var(--border-subtle)',
            background: 'var(--bg-base)',
            padding: '10px 12px',
          }}>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', fontWeight: 600, lineHeight: 1.5 }}>
              {authority.title}
            </p>
            <p style={{ margin: '4px 0 0', fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
              {authority.court} · {authority.tier} {authority.trustLabel ? `· ${authority.trustLabel}` : ''}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ResearchSignalsCard({ title, subtitle, icon: Icon, items, emptyState, tone = 'neutral', testId }) {
  const accentColor = tone === 'warning' ? '#9a5937' : 'var(--accent-primary)';
  const accentBg = tone === 'warning' ? 'rgba(184, 92, 56, 0.12)' : 'var(--accent-glow)';

  return (
    <div
      data-testid={testId}
      style={{
        borderRadius: '18px',
        border: '1px solid color-mix(in srgb, var(--border-default) 82%, transparent)',
        background: 'var(--app-panel-bg)',
        padding: '16px 18px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <div style={{
          width: '28px',
          height: '28px',
          borderRadius: '10px',
          background: accentBg,
          border: `1px solid ${tone === 'warning' ? 'rgba(184, 92, 56, 0.2)' : 'rgba(46,204,90,0.2)'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Icon size={14} style={{ color: accentColor }} />
        </div>
        <div>
          <p style={{ margin: 0, fontSize: '11px', color: accentColor, fontFamily: 'var(--font-ui)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {title}
          </p>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
            {subtitle}
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {items.length === 0 && (
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
            {emptyState}
          </p>
        )}
        {items.map(item => (
          <div key={item.id} style={{
            borderRadius: '12px',
            border: '1px solid var(--border-subtle)',
            background: 'var(--bg-elevated)',
            padding: '10px 12px',
          }}>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', fontWeight: 600 }}>
              {item.title}
            </p>
            <p style={{ margin: '5px 0 0', fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)', lineHeight: 1.55 }}>
              {item.detail}
            </p>
            {item.sourceTitle && (
              <p style={{ margin: '5px 0 0', fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
                From: {item.sourceTitle}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function WorkflowWorkbenchCard({ workflowMode, currentFlow, onSwitchMode, onApplyTemplate }) {
  return (
    <div style={{
      borderRadius: '18px',
      border: '1px solid color-mix(in srgb, var(--border-default) 82%, transparent)',
      background: 'var(--app-panel-bg)',
      padding: '16px 18px',
      marginBottom: '22px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <div>
          <p style={{ margin: 0, fontSize: '11px', color: 'var(--accent-primary)', fontFamily: 'var(--font-ui)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Premium Workflow
          </p>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
            Move between live legal work modes without leaving the desk.
          </p>
        </div>
        <ContextBadge label={`Active: ${currentFlow.label}`} />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {WORKSPACE_FLOWS.filter(flow => flow.value !== 'IRAC').map(flow => (
          <button
            key={flow.value}
            onClick={() => onSwitchMode(flow.value)}
            style={{
              textAlign: 'left',
              borderRadius: '14px',
              border: `1px solid ${workflowMode === flow.value ? 'rgba(46,204,90,0.3)' : 'var(--border-subtle)'}`,
              background: workflowMode === flow.value ? 'var(--accent-glow)' : 'var(--bg-elevated)',
              padding: '12px 14px',
              cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <flow.icon size={14} style={{ color: workflowMode === flow.value ? 'var(--accent-primary)' : 'var(--text-muted)' }} />
              <strong style={{ fontSize: '12px', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>{flow.label}</strong>
            </div>
            <p style={{ margin: 0, fontSize: '11px', color: workflowMode === flow.value ? 'var(--text-secondary)' : 'var(--text-muted)', fontFamily: 'var(--font-ui)', lineHeight: 1.5 }}>
              {flow.helper}
            </p>
          </button>
        ))}
      </div>

      <div className="mt-3 flex overflow-x-auto pb-1 md:justify-end md:overflow-visible md:pb-0">
        <button
          onClick={() => onApplyTemplate(currentFlow.template)}
          className="shrink-0 whitespace-nowrap"
          style={{
            padding: '8px 12px',
            borderRadius: '10px',
            border: '1px solid var(--border-default)',
            background: 'var(--bg-elevated)',
            color: 'var(--text-secondary)',
            fontSize: '11px',
            cursor: 'pointer',
            fontFamily: 'var(--font-ui)',
            fontWeight: 600,
          }}
        >
          Reframe section outline
        </button>
      </div>
    </div>
  );
}

function KickoffBriefCard({ loading, status, report, cases, docs, onOpenPreview, onInsert }) {
  return (
    <div className="research-agent-note-card" style={{
      borderRadius: '22px',
      border: '1px solid color-mix(in srgb, var(--border-default) 78%, transparent)',
      background: 'var(--app-panel-bg)',
      padding: '18px 20px',
      marginBottom: '28px',
      boxShadow: '0 16px 36px rgba(39, 28, 18, 0.08)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div>
          <p style={{ margin: 0, fontSize: '11px', color: 'var(--accent-primary)', fontFamily: 'var(--font-ui)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Research Brief
          </p>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>
            Verified overview generated from Indian Kanoon before you start drafting.
          </p>
        </div>
        {report && (
          <button
            onClick={onInsert}
            style={{
              padding: '8px 12px',
              borderRadius: '10px',
              border: '1px solid var(--border-default)',
              background: 'var(--bg-elevated)',
              color: 'var(--text-secondary)',
              fontSize: '11px',
              cursor: 'pointer',
              fontFamily: 'var(--font-ui)',
              fontWeight: 600,
            }}
          >
            Insert into document
          </button>
        )}
      </div>

      {loading && (
        <div style={{ marginTop: '16px' }}>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
          </div>
          <p style={{ margin: '12px 0 0', color: 'var(--accent-primary)', fontFamily: 'var(--font-ui)', fontSize: '12px' }}>
            {status || 'Generating verified research brief...'}
          </p>
        </div>
      )}

      {report && (
        <div
          className="markdown-content max-h-none md:max-h-[340px]"
          style={{
            marginTop: '16px',
            overflowY: 'auto',
            fontFamily: 'var(--font-ui)',
            fontSize: '13px',
          }}
          onClick={e => {
            const anchor = e.target.closest('a[href]');
            if (anchor) {
              e.preventDefault();
              onOpenPreview(anchor.href, anchor.textContent.trim());
            }
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{report}</ReactMarkdown>
        </div>
      )}

      {(cases.length > 0 || docs.length > 0) && (
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '16px', paddingTop: '14px', borderTop: '1px solid var(--border-subtle)' }}>
          {cases.length > 0 && (
            <div style={{ minWidth: '220px', flex: 1 }}>
              <p style={kickoffLabelStyle}>Verified authorities</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {cases.slice(0, 3).map(item => (
                  <button
                    key={item.id}
                    onClick={() => onOpenPreview(item.url, item.title)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      textAlign: 'left',
                      cursor: 'pointer',
                      color: 'var(--text-secondary)',
                      fontSize: '11px',
                      fontFamily: 'var(--font-ui)',
                    }}
                  >
                    {item.title}
                  </button>
                ))}
              </div>
            </div>
          )}
          {docs.length > 0 && (
            <div style={{ minWidth: '220px', flex: 1 }}>
              <p style={kickoffLabelStyle}>Your documents used</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {docs.slice(0, 3).map(doc => (
                  <span
                    key={`${doc.upload_id || doc.filename}-${doc.filename}`}
                    style={{ color: 'var(--text-secondary)', fontSize: '11px', fontFamily: 'var(--font-ui)' }}
                  >
                    {doc.filename}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResearchContextCard({ items, onOpenPreview }) {
  const matterFileCount = items.filter(item => item?.trust_label === 'Matter File').length;
  const jollyCount = items.filter(item => item?.trust_label === 'Jolly-Derived Matter Memory').length;
  return (
    <div style={{
      borderRadius: '18px',
      border: '1px solid color-mix(in srgb, var(--border-default) 78%, transparent)',
      background: 'var(--bg-elevated)',
      padding: '14px 16px',
      marginBottom: '22px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <div>
          <p style={{ margin: 0, fontSize: '11px', color: 'var(--accent-primary)', fontFamily: 'var(--font-ui)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Matter Context Used
          </p>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
            Only reviewed matter material is pulled in, with provenance kept visible.
          </p>
        </div>
        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>
          {items.length} context item{items.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <span style={{ ...chipStyle, background: 'var(--bg-base)', color: 'var(--text-secondary)' }}>
          Matter files · {matterFileCount}
        </span>
        <span style={{ ...chipStyle, background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}>
          Jolly memory · {jollyCount}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {items.slice(0, 5).map(item => (
          <div
            key={item.artifact_id || item.title}
            style={{
              borderRadius: '12px',
              border: '1px solid var(--border-subtle)',
              background: 'var(--bg-base)',
              padding: '10px 12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
              <strong style={{ fontSize: '12px', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>{item.title}</strong>
              <SourceTrustBadge label={item.trust_label} compact />
              {item.review_state && (
                <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
                  {String(item.review_state).replace(/_/g, ' ')}
                </span>
              )}
            </div>
            <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)', lineHeight: 1.6 }}>
              {item.reason}
            </p>
            {item.snippet && (
              <p style={{ margin: '6px 0 0', fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)', lineHeight: 1.6 }}>
                {item.snippet}
              </p>
            )}
            {item.preview_url && (
              <button
                onClick={() => onOpenPreview(item.preview_url, item.title)}
                style={{ ...chipStyle, marginTop: '8px' }}
              >
                <ExternalLink size={10} /> Open source
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const kickoffLabelStyle = {
  margin: '0 0 8px',
  fontSize: '11px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: 'var(--text-muted)',
  fontFamily: 'var(--font-ui)',
};

function StatusPill({ icon: Icon, label, active }) {
  return (
    <div className="shrink-0 whitespace-nowrap" style={{
      display: 'flex', alignItems: 'center', gap: '5px',
      fontSize: '11px',
      color: active ? 'var(--accent-primary)' : 'var(--text-muted)',
      fontFamily: 'var(--font-ui)',
      padding: '6px 10px',
      borderRadius: '999px',
      border: `1px solid ${active ? 'color-mix(in srgb, var(--accent-primary) 20%, var(--border-default))' : 'color-mix(in srgb, var(--border-default) 76%, transparent)'}`,
      background: active ? 'var(--accent-glow)' : 'var(--bg-elevated)',
    }}>
      <Icon size={12} style={{ color: active ? 'var(--accent-primary)' : 'var(--text-muted)' }} />
      {label}
    </div>
  );
}

function Divider() {
  return <div style={{ width: '1px', height: '16px', background: 'var(--border-subtle)', flexShrink: 0 }} />;
}

function ToolbarBtn({ onClick, icon, label, active }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '4px',
        padding: '4px 9px', borderRadius: '6px', border: 'none', cursor: 'pointer',
        background: active ? 'var(--bg-elevated)' : 'transparent',
        color: active ? 'var(--text-primary)' : 'var(--text-muted)',
        fontSize: '12px', fontFamily: 'var(--font-ui)',
        transition: 'all 0.12s',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elevated)')}
      onMouseLeave={e => (e.currentTarget.style.background = active ? 'var(--bg-elevated)' : 'transparent')}
    >
      {icon}
      {label}
    </button>
  );
}

function SectionLabel({ label }) {
  return (
    <p style={{
      fontSize: '9px', fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: '0.12em', color: 'var(--text-muted)',
      padding: '6px 4px 4px', fontFamily: 'var(--font-ui)',
    }}>
      {label}
    </p>
  );
}

function IssueGroupLabel({ label, count }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '6px',
      padding: '8px 4px 4px', marginTop: '2px',
    }}>
      <Tag size={9} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
      <span style={{
        fontSize: '10px', fontWeight: 600, color: 'var(--text-secondary)',
        fontFamily: 'var(--font-ui)',
        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }} title={label}>
        {label}
      </span>
      <span style={{
        fontSize: '9px', padding: '1px 5px', borderRadius: '8px',
        background: 'var(--bg-surface)', color: 'var(--text-muted)',
        border: '1px solid var(--border-subtle)',
        fontFamily: 'var(--font-ui)', flexShrink: 0,
      }}>
        {count}
      </span>
    </div>
  );
}

function UserDocCard({ doc, isActive, onToggle, onOpenPreview, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const ext = doc.filename?.split('.').pop()?.toUpperCase() || 'FILE';
  const sizeKB = doc.file_size ? Math.round(doc.file_size / 1024) : null;

  return (
    <div style={{
      borderRadius: '8px', marginBottom: '5px', overflow: 'hidden',
      border: `1px solid ${isActive ? 'rgba(46,204,90,0.4)' : 'var(--border-subtle)'}`,
      background: isActive ? 'rgba(46,204,90,0.04)' : 'var(--bg-elevated)',
      transition: 'border-color 0.15s, background 0.15s',
    }}>
      <div style={{ padding: '8px 9px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px' }}>
          {/* Checkbox */}
          <input
            type="checkbox"
            checked={isActive}
            onChange={onToggle}
            style={{ marginTop: '2px', accentColor: 'var(--accent-primary)', flexShrink: 0 }}
          />
          {/* File icon + name */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '2px' }}>
              <File size={11} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
              <span style={{
                fontSize: '11px', fontWeight: 600, color: 'var(--text-primary)',
                fontFamily: 'var(--font-ui)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {doc.filename}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{
                fontSize: '9px', padding: '1px 5px', borderRadius: '3px',
                background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                color: 'var(--text-muted)', fontFamily: 'var(--font-ui)',
              }}>
                {ext}
              </span>
              {sizeKB && (
                <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
                  {sizeKB > 1024 ? `${(sizeKB / 1024).toFixed(1)}MB` : `${sizeKB}KB`}
                </span>
              )}
              {doc.word_count && (
                <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
                  · {doc.word_count.toLocaleString()} words
                </span>
              )}
            </div>
          </div>
          {/* Actions */}
          <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
            <button
              onClick={onOpenPreview}
              title="Open full preview"
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                color: 'var(--text-muted)', display: 'flex', alignItems: 'center',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--accent-primary)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}
            >
              <ExternalLink size={11} />
            </button>
            <button
              onClick={() => setExpanded(v => !v)}
              title="Preview summary"
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                color: expanded ? 'var(--accent-primary)' : 'var(--text-muted)',
                display: 'flex', alignItems: 'center',
              }}
            >
              <ChevronRight size={12} style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
            </button>
            <button
              onClick={onDelete}
              title="Remove from knowledge base"
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                color: 'var(--text-muted)', display: 'flex', alignItems: 'center',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = '#e74c3c')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}
            >
              <Trash2 size={11} />
            </button>
          </div>
        </div>

        {/* Summary preview */}
        {expanded && doc.summary && (
          <div style={{
            marginTop: '8px', padding: '7px 9px',
            background: 'var(--bg-surface)', borderRadius: '5px',
            fontSize: '11px', color: 'var(--text-secondary)',
            fontFamily: 'var(--font-ui)', lineHeight: 1.5,
          }}>
            {doc.summary}
            {doc.keywords?.length > 0 && (
              <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                {doc.keywords.slice(0, 8).map((kw, i) => (
                  <span key={i} style={{
                    fontSize: '9px', padding: '1px 5px', borderRadius: '3px',
                    background: 'var(--accent-glow)', color: 'var(--accent-primary)',
                    border: '1px solid rgba(46,204,90,0.2)',
                  }}>
                    {kw}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SourceListSkeleton({ title, description }) {
  return (
    <div className="animate-pulse space-y-3 py-4">
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/40 p-4">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
        <div className="mt-3 h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
        <div className="mt-3 h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
      </div>
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/40 p-4 space-y-3">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/5" />
      </div>
      <p className="text-center text-xs text-gray-400 px-3">
        Searching for {title}. {description}
      </p>
    </div>
  );
}

function DocumentShelfSkeleton() {
  return (
    <div className="animate-pulse space-y-3 py-4">
      {[0, 1, 2].map((item) => (
        <div
          key={item}
          className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/40 p-4"
        >
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
          <div className="mt-3 h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
          <div className="mt-3 h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
        </div>
      ))}
    </div>
  );
}

function EmptySourcesState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Search className="w-12 h-12 text-gray-300 mb-4" />
      <h3 className="text-lg font-medium text-gray-600 mb-2">Start your research</h3>
      <p className="text-sm text-gray-400 max-w-xs">
        Search Indian Kanoon or open a live desk note to build an authority stack with verified sources and draft-ready context.
      </p>
    </div>
  );
}

function SourceCard({ source, isPinned, isActive, isExpanded, onToggleActive, onTogglePin, onExpand, onOpenPreview, onRemove }) {
  return (
    <div
      style={{
        borderRadius: '8px', marginBottom: '5px', overflow: 'hidden',
        border: `1px solid ${isActive ? 'rgba(46,204,90,0.4)' : 'var(--border-subtle)'}`,
        background: isActive ? 'rgba(46,204,90,0.04)' : 'var(--bg-elevated)',
        transition: 'border-color 0.15s, background 0.15s',
      }}
    >
      <div style={{ padding: '8px 9px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px' }}>
          {/* Checkbox */}
          <button
            onClick={onToggleActive}
            title={isActive ? 'Remove from context' : 'Add to agent context'}
            style={{
              width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0, marginTop: '2px',
              border: `2px solid ${isActive ? 'var(--accent-primary)' : 'var(--border-default)'}`,
              background: isActive ? 'var(--accent-primary)' : 'transparent',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {isActive && <CheckCircle size={9} style={{ color: 'var(--accent-foreground)' }} />}
          </button>

          {/* Info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              fontSize: '11px', fontWeight: 500, color: 'var(--text-primary)',
              fontFamily: 'var(--font-ui)', lineHeight: '1.4', marginBottom: '3px',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {source.title || 'Untitled'}
            </p>
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              {source.trust_label && (
                <SourceTrustBadge label={source.trust_label} compact />
              )}
              {source.docsource && (
                <span
                  className="max-w-full truncate"
                  title={source.docsource}
                  style={{
                  fontSize: '9px', padding: '1px 5px', borderRadius: '3px',
                  background: 'var(--bg-base)', color: 'var(--text-muted)',
                  fontFamily: 'var(--font-ui)',
                }}>
                  {source.docsource}
                </span>
              )}
              {source.authority_strength_label && (
                <span style={{ fontSize: '9px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
                  {source.authority_strength_label}
                </span>
              )}
              {source.publishdate && (
                <span style={{ fontSize: '9px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
                  {source.publishdate}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action row */}
        <div style={{ display: 'flex', gap: '3px', marginTop: '7px' }}>
          <button
            onClick={onExpand}
            style={chipStyle}
          >
            {isExpanded ? '↑ Less' : '↓ Preview'}
          </button>
          <button
            onClick={() => onOpenPreview(source.url, source.title)}
            style={{ ...chipStyle, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '3px' }}
          >
            <ExternalLink size={9} /> Open
          </button>
          <button
            onClick={onTogglePin}
            title={isPinned ? 'Unpin' : 'Pin'}
            style={{
              ...chipStyle,
              background: isPinned ? 'rgba(46,204,90,0.12)' : 'var(--bg-base)',
              color: isPinned ? 'var(--accent-primary)' : 'var(--text-muted)',
            }}
          >
            📌
          </button>
          <button
            onClick={onRemove}
            title="Remove"
            style={{ ...chipStyle, color: '#e74c3c', marginLeft: 'auto' }}
          >
            <X size={10} />
          </button>
        </div>
      </div>

      {/* Expanded preview */}
      {isExpanded && source.headline && (
        <div style={{
          padding: '8px 10px',
          borderTop: '1px solid var(--border-subtle)',
          background: 'var(--bg-base)',
        }}>
          {source.reason && (
            <p style={{
              fontSize: '10px',
              color: 'var(--accent-primary)',
              lineHeight: '1.5',
              fontFamily: 'var(--font-ui)',
              marginBottom: '6px',
            }}>
              {source.reason}
            </p>
          )}
          <p
            style={{
              fontSize: '11px', color: 'var(--text-secondary)', lineHeight: '1.6',
              fontFamily: 'var(--font-ui)',
            }}
            dangerouslySetInnerHTML={{ __html: source.headline }}
          />
        </div>
      )}
    </div>
  );
}

const chipStyle = {
  padding: '3px 6px', borderRadius: '4px', border: 'none',
  background: 'var(--bg-base)', color: 'var(--text-muted)',
  fontSize: '10px', cursor: 'pointer', fontFamily: 'var(--font-ui)',
  display: 'flex', alignItems: 'center', gap: '3px',
};

function DocToolbarBtn({ onClick, active, title, children }) {
  return (
    <button
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      title={title}
      style={{
        padding: '3px 6px', borderRadius: '4px', border: 'none', cursor: 'pointer',
        background: active ? 'var(--bg-hover)' : 'transparent',
        color: active ? 'var(--text-primary)' : 'var(--text-muted)',
        fontFamily: 'var(--font-ui)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.1s, color 0.1s',
        minWidth: '24px', height: '24px',
      }}
    >
      {children}
    </button>
  );
}

const ToolbarSep = () => (
  <div style={{ width: '1px', height: '14px', background: 'var(--border-subtle)', margin: '0 3px', flexShrink: 0 }} />
);

function DocumentSection({ section, index, total, isGenerating, sectionRef, onChange, onRemove, onMoveUp, onMoveDown, onGenerate }) {
  const isInternalRef = useRef(false);
  const prevGeneratingRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: section.placeholder || 'Start writing or ask the agent to write this section...',
      }),
      TiptapMarkdown.configure({
        html: false,
        transformCopiedText: true,
        transformPastedText: true,
      }),
    ],
    content: section.content || '',
    onUpdate: ({ editor }) => {
      isInternalRef.current = true;
      const md = editor.storage.markdown.getMarkdown();
      onChange('content', md);
      requestAnimationFrame(() => { isInternalRef.current = false; });
    },
    editorProps: {
      attributes: { class: 'doc-tiptap-editor', spellcheck: 'true' },
    },
  });

  // After AI finishes generating, sync the final markdown into the editor
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (isGenerating) { prevGeneratingRef.current = true; return; }
    if (prevGeneratingRef.current) {
      prevGeneratingRef.current = false;
      editor.commands.setContent(section.content || '');
    }
  }, [isGenerating]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!editor) return null;

  return (
    <div ref={sectionRef} style={{ marginBottom: '28px' }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <span style={{
          fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'monospace',
          minWidth: '20px', textAlign: 'center',
        }}>
          {index + 1}
        </span>
        <input
          value={section.title}
          onChange={e => onChange('title', e.target.value)}
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none',
            fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)',
            fontFamily: 'var(--font-ui)',
          }}
        />
        <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
          <button
            onClick={onGenerate}
            disabled={isGenerating}
            title="Generate with AI"
            style={{
              padding: '3px 8px', borderRadius: '5px',
              border: '1px solid rgba(46,204,90,0.3)',
              background: 'rgba(46,204,90,0.08)', color: 'var(--accent-primary)',
              fontSize: '11px', cursor: isGenerating ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', gap: '4px',
              fontFamily: 'var(--font-ui)',
            }}
          >
            {isGenerating ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
            AI
          </button>
          <button onClick={onMoveUp} disabled={index === 0} title="Move up"
            style={{ ...sectionBtnStyle, opacity: index === 0 ? 0.3 : 1, cursor: index === 0 ? 'not-allowed' : 'pointer' }}>
            <ArrowUp size={11} />
          </button>
          <button onClick={onMoveDown} disabled={index === total - 1} title="Move down"
            style={{ ...sectionBtnStyle, opacity: index === total - 1 ? 0.3 : 1, cursor: index === total - 1 ? 'not-allowed' : 'pointer' }}>
            <ArrowDown size={11} />
          </button>
          <button onClick={onRemove} title="Remove section" style={{ ...sectionBtnStyle, color: '#e74c3c' }}>
            <Trash2 size={11} />
          </button>
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: '1px', background: 'var(--border-subtle)', marginBottom: '8px' }} />

      {/* Formatting toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '1px', flexWrap: 'wrap',
        padding: '3px 5px', marginBottom: '6px',
        background: 'var(--bg-base)', borderRadius: '6px',
        border: '1px solid var(--border-subtle)',
      }}>
        <DocToolbarBtn title="Heading 1"
          active={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
          <span style={{ fontSize: '10px', fontWeight: 800, letterSpacing: '-0.3px' }}>H1</span>
        </DocToolbarBtn>
        <DocToolbarBtn title="Heading 2"
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <span style={{ fontSize: '10px', fontWeight: 800, letterSpacing: '-0.3px' }}>H2</span>
        </DocToolbarBtn>
        <DocToolbarBtn title="Heading 3"
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <span style={{ fontSize: '10px', fontWeight: 800, letterSpacing: '-0.3px' }}>H3</span>
        </DocToolbarBtn>
        <ToolbarSep />
        <DocToolbarBtn title="Bold (Ctrl+B)"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}>
          <span style={{ fontWeight: 900, fontSize: '13px' }}>B</span>
        </DocToolbarBtn>
        <DocToolbarBtn title="Italic (Ctrl+I)"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}>
          <span style={{ fontStyle: 'italic', fontSize: '13px' }}>I</span>
        </DocToolbarBtn>
        <DocToolbarBtn title="Strikethrough"
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}>
          <span style={{ textDecoration: 'line-through', fontSize: '12px' }}>S</span>
        </DocToolbarBtn>
        <ToolbarSep />
        <DocToolbarBtn title="Bullet list"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List size={12} />
        </DocToolbarBtn>
        <DocToolbarBtn title="Numbered list"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <span style={{ fontSize: '10px', fontFamily: 'monospace', fontWeight: 700 }}>1.</span>
        </DocToolbarBtn>
        <DocToolbarBtn title="Blockquote"
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <span style={{ fontSize: '15px', lineHeight: 1, fontWeight: 700 }}>"</span>
        </DocToolbarBtn>
        <DocToolbarBtn title="Code block"
          active={editor.isActive('codeBlock')}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
          <span style={{ fontFamily: 'monospace', fontSize: '10px' }}>{`</>`}</span>
        </DocToolbarBtn>
        <ToolbarSep />
        <DocToolbarBtn title="Horizontal rule" active={false}
          onClick={() => editor.chain().focus().setHorizontalRule().run()}>
          <span style={{ fontSize: '12px' }}>—</span>
        </DocToolbarBtn>
      </div>

      {/* WYSIWYG editor body */}
      {isGenerating ? (
        <div style={{
          minHeight: '80px', display: 'flex', alignItems: 'center', gap: '8px',
          color: 'var(--text-muted)', fontSize: '13px', fontFamily: 'var(--font-ui)',
          padding: '12px 0',
        }}>
          <Loader2 size={14} className="animate-spin" style={{ color: 'var(--accent-primary)' }} />
          Writing…
        </div>
      ) : (
        <EditorContent editor={editor} />
      )}
    </div>
  );
}

const sectionBtnStyle = {
  padding: '4px', borderRadius: '4px', border: 'none',
  background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

function AgentMessage({ message, openPreview }) {
  if (message.type === 'intro') {
    return (
      <div className="research-agent-message research-agent-message--intro" style={{
        padding: '12px 14px', borderRadius: '10px',
        background: 'var(--bg-elevated)',
        border: '1px solid color-mix(in srgb, var(--border-default) 82%, transparent)',
        fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.65',
        fontFamily: 'var(--font-ui)',
      }}>
        {message.content}
      </div>
    );
  }

  if (message.type === 'topic_set' || message.type === 'sources_added') {
    return (
      <div style={{ textAlign: 'center' }}>
        <span className="research-agent-message research-agent-message--system" style={{
          display: 'inline-block', padding: '3px 11px', borderRadius: '10px',
          background: 'var(--accent-glow)',
          border: '1px solid rgba(46,204,90,0.2)',
          fontSize: '11px', color: 'var(--accent-primary)',
          fontFamily: 'var(--font-ui)',
        }}>
          ✓ {message.content}
        </span>
      </div>
    );
  }

  const isAgent = message.role === 'agent';

  return (
    <div style={{ display: 'flex', gap: '8px', flexDirection: isAgent ? 'row' : 'row-reverse' }}>
      {isAgent && (
        <div style={{
          width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
          background: 'var(--accent-glow)', border: '1px solid rgba(46,204,90,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginTop: '3px',
        }}>
          <Bot size={12} style={{ color: 'var(--accent-primary)' }} />
        </div>
      )}
      <div className={`research-agent-message ${isAgent ? 'research-agent-message--agent' : 'research-agent-message--user'}`} style={{
        maxWidth: '86%',
        padding: isAgent ? '2px 0' : '8px 12px',
        borderRadius: isAgent ? '0' : '10px',
        background: isAgent ? 'transparent' : 'var(--accent-glow)',
        border: isAgent ? 'none' : '1px solid rgba(46,204,90,0.2)',
        borderLeft: isAgent ? '2px solid color-mix(in srgb, var(--accent-primary) 30%, transparent)' : 'none',
        paddingLeft: isAgent ? '10px' : '12px',
        fontSize: '12px', lineHeight: '1.72',
        fontFamily: 'var(--font-ui)', color: 'var(--text-primary)',
      }}>
        {isAgent ? (
          <div
            className="markdown-content"
            style={{ fontSize: '12px' }}
            onClick={e => {
              const a = e.target.closest('a[href]');
              if (a && openPreview) { e.preventDefault(); openPreview(a.href, a.textContent.trim()); }
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {message.content || (message.streaming ? '' : '')}
            </ReactMarkdown>
            {message.streaming && (
              <span style={{
                display: 'inline-block', width: '6px', height: '13px',
                background: 'var(--accent-primary)', marginLeft: '2px', verticalAlign: 'middle',
                animation: 'blink 1s step-end infinite',
              }} />
            )}
          </div>
        ) : (
          <span style={{ color: 'var(--text-primary)' }}>{message.content}</span>
        )}
      </div>
    </div>
  );
}
